import { AuthStorage } from "utils/auth-storage";
import {
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Input,
  Modal,
  Popover,
  Select,
  Tooltip,
} from "antd";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  BoxPlotOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  FilterFilled,
  FlagOutlined,
  FormOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudIcon from "@mui/icons-material/Cloud";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import SyncIcon from "@mui/icons-material/Sync";
import BulkDelete from "components/bulk-delete";
import { ApiUrl } from "apis/Domains";
import { ProjectServices } from "apis/Services/ProjectService";
import { HiddenItemsService } from "apis/Services/HiddenItemsService";
import { CriticalPathFlagItemsService } from "apis/Services/CriticalPathFlagItemsService";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ProcoreLogo from "assets/images/Procore_Logo.svg";
import PostSubmittals from "components/post-submittals";
import SidebarDistribution from "components/sidebar-distribution/sidebar-distribution";
import PostToProcoreModal from "pages/warehouse-inventory/post-to-procore-modal";
import { Link, useNavigate, useParams } from "react-router-dom";
import LoadingComponent from "components/loading-component";
import ProcurementActiveUsers from "./active-users";
import ProcurementBulkEditModal from "./bulk-edit-modal";
import ExportProcurementLogPdf from "./ExportProcurementLogPdf";
import ImportProcurementLog from "./import-procurement-log";
import ImportSchedule from "./import-schedule";
import ItemsShippedModal from "./items-shipped-modal";
import ProcurementSideMenuContent from "./ProcurementSideMenuContent";
import styles from "./ProcurementLogPage.module.css";
import { SideBarEndService } from "apis/Services/SideBarService";
import { WarehouseServices } from "apis/Services/WarehouseService";
import {
  applyFormulaColumnsToRow,
  applyFormulaColumnsToRows,
  applyScheduleIdClearingPatch,
  buildFormulaColumns,
  buildScheduleSyncContext,
  createNewSubmittalDraft,
} from "./procurementCalculations";
import {
  buildBusinessCalendarContext,
  FORMULA_DATE_FORMAT,
  firstFilled,
  getProcurementFamilyRootId,
  getGroupingSettings,
  normalizeText,
  parseFormulaDate,
  buildColumnPermissions,
} from "./procurementHelpers";
import {
  buildSelectionDescendantsLookup,
  buildProcurementTree,
  filterRowsByAccessibleItems,
  filterRowsBySubmittalSearch,
  flattenProcurementRows,
  reconcileExpandedState,
} from "./procurementTree";
import {
  buildColumnDefs,
  ProcurementGroupFullWidthRow,
  SUBMITTAL_DROPDOWN_SELECTION_MARKER,
} from "./procurementColumns";
import {
  buildDependencyStateByEntryId,
  getWorkflowLockStateForRow,
} from "./procurementDependencies";
import { useProcurementData } from "./useProcurementData";
import { useProcurementLogPosition } from "./useProcurementLogPosition";
import { useProcurementEditFeedback } from "./useProcurementEditFeedback";
import Settings from "components/settings-modal";
import { HistoryOutlined } from "@material-ui/icons";
import {
  ENTRY_ORIGIN_OPTIONS,
  filterRowsByAdvancedFilters,
  filterRowsByColumnFilters,
} from "./procurementFilters";
import ItemsPendingApprovalsModal from "./itemsPendingApprovalsModal";
import { SetProjectName, projectsAccessLevel } from "redux/project-reducer";
import { useDispatch } from "react-redux";

const DEFAULT_SYNC_STATUS = {
  state: "synced",
  message: "All changes synced",
};
const DEFAULT_SYNC_ERROR_MESSAGE =
  "Something went wrong. You may no longer be synced to the latest version.";
const DEFAULT_SOCKET_SYNC_ERROR_MESSAGE =
  "Live sync is unavailable. You may no longer be synced to the latest version.";

// Renders the top toolbar actions above the procurement log.
const ProcurementTopBar = ({
  company_id,
  project_id,
  accessLevel,
  selectedView,
  viewOptions,
  onUpdateView,
  isViewDisabled,
  exportDistributeMenuProps,
  actionsMenuProps,
  itemsPendingApprovals,
  deletedEntryIds,
  projectName,
  isLoading,
  showWarehouseTransfers,
  isWarehouseTransfersDisabled,
  onOpenWarehouseTransfers,
  reloadData,
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
}) => (
  <div
    className={`d-flex align-items-center justify-content-between px-4 py-2 ${styles.toolbar}`}
  >
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <Settings
        projectsAccessLevelValue={accessLevel}
        isHideWarningStyles={true}
        onSaveSuccess={reloadData}
      />
      <Select
        showSearch
        placeholder="Select a View"
        className={styles.viewSelect}
        disabled={isViewDisabled}
        style={{ width: "220px" }}
        popupMatchSelectWidth={false}
        onChange={onUpdateView}
        value={selectedView}
        options={viewOptions}
        optionFilterProp="label"
      />
    </div>
    <div className="d-flex align-items-center gap-2">
      {showWarehouseTransfers && (
        <Button
          className={styles.antBtn}
          disabled={isWarehouseTransfersDisabled}
          onClick={onOpenWarehouseTransfers}
        >
          Warehouse Transfers
        </Button>
      )}
      {accessLevel > 1 && (
        <>
          <ItemsPendingApprovalsModal
            company_id={company_id}
            project_id={project_id}
            accessLevel={accessLevel}
            isLoading={isLoading}
            itemsPendingApprovals={itemsPendingApprovals}
            deletedEntryIds={deletedEntryIds}
            projectName={projectName}
            beginSyncActivity={beginSyncActivity}
            finishSyncActivity={finishSyncActivity}
            failSyncActivity={failSyncActivity}
            // onSuccess={reloadData}
          />
        </>
      )}

      <Dropdown
        menu={actionsMenuProps}
        placement="bottomLeft"
        trigger={["click"]}
      >
        <Button className={styles.antBtn}>
          Actions
          <DownOutlined style={{ fontSize: "14px" }} />
        </Button>
      </Dropdown>

      <Dropdown
        menu={exportDistributeMenuProps}
        placement="bottomLeft"
        trigger={["click"]}
      >
        <Button className={styles.antBtn}>
          Export & Distribute
          <DownOutlined style={{ fontSize: "14px" }} />
        </Button>
      </Dropdown>
    </div>
  </div>
);

const SyncStatusIndicator = ({ syncState, syncMessage, onRetrySync }) => {
  const normalizedSyncState = firstFilled(syncState, "synced");
  const isSyncing = normalizedSyncState === "syncing";
  const isError = normalizedSyncState === "error";
  const tooltipMessage = isError
    ? `${firstFilled(syncMessage) || DEFAULT_SYNC_STATUS.message} Click to refresh the latest data.`
    : firstFilled(syncMessage) || DEFAULT_SYNC_STATUS.message;
  const SyncStatusIcon = isError
    ? CloudOffIcon
    : isSyncing
      ? SyncIcon
      : normalizedSyncState === "synced"
        ? CloudDoneIcon
        : CloudIcon;
  const cloudStateClassName = isError
    ? styles.syncCloudError
    : isSyncing
      ? styles.syncCloudSyncing
      : styles.syncCloudSynced;
  const handleClick = isError ? onRetrySync : undefined;
  const IndicatorTag = isError ? "button" : "span";

  return (
    <Tooltip title={tooltipMessage} placement="top" color="#1f1f1f">
      <IndicatorTag
        type={isError ? "button" : undefined}
        className={`${styles.syncStatusIndicator} ${
          isError ? styles.syncStatusIndicatorClickable : ""
        }`}
        aria-label={tooltipMessage}
        onClick={handleClick}
      >
        <span className={styles.syncStatusIconStack}>
          <SyncStatusIcon
            className={`${styles.syncCloudIcon} ${cloudStateClassName} ${
              isSyncing ? styles.syncCloudSpinning : ""
            }`}
          />
        </span>
      </IndicatorTag>
    </Tooltip>
  );
};

// Renders search, selection, and save controls above the grid.
const ProcurementQuickBar = ({
  company_id,
  project_id,
  accessLevel,
  searchValue,
  onSearchChange,
  submittalsTotalCount,
  selectedCount,
  isSubscribedToWarehouse,
  canCreatePurchaseOrder,
  onOpenCreatePurchaseOrder,
  bulkMenuProps,
  onClearSelection,
  activeFilterCount,
  onClearFilters,
  showMyAccessibleToggle,
  isMyAccessibleOnly,
  onToggleMyAccessibleOnly,
  advancedFilters,
  onChangeAdvancedFilters,
  assignedFilterOptions,
  syncState,
  syncMessage,
  onRetrySync,
}) => (
  <div
    className={`d-flex align-items-center justify-content-between px-3 py-2 ${styles.quickBar}`}
  >
    <div className="d-flex gap-2 align-items-center">
      <Input
        placeholder="Search by title or number..."
        className={`rounded-5 ${styles.searchInput}`}
        size="large"
        value={searchValue}
        allowClear
        onChange={(event) => onSearchChange?.(event.target.value)}
        style={{ width: 250 }}
      />
      <ProcurementActiveUsers company_id={company_id} project_id={project_id} />
      <Popover
        trigger="click"
        placement="bottomLeft"
        overlayClassName={styles.advancedFiltersPopover}
        content={
          <div className={styles.advancedFiltersPanel}>
            <div className={styles.advancedFiltersSection}>
              <span className={styles.advancedFiltersLabel}>Status</span>
              <Checkbox.Group
                className={styles.advancedFiltersCheckboxGroup}
                value={advancedFilters.statuses}
                onChange={(nextStatuses) =>
                  onChangeAdvancedFilters?.({
                    ...advancedFilters,
                    statuses: nextStatuses,
                  })
                }
                options={[
                  { label: "Drafts", value: "draft" },
                  { label: "Pending", value: "pending" },
                  { label: "Overdue", value: "overdue" },
                  { label: "Completed", value: "completed" },
                ]}
              />
            </div>

            <div className={styles.advancedFiltersSection}>
              <span className={styles.advancedFiltersLabel}>Entry Origin</span>
              <Checkbox.Group
                className={styles.advancedFiltersCheckboxGroup}
                value={advancedFilters.entryOrigins}
                options={ENTRY_ORIGIN_OPTIONS}
                onChange={(entryOrigins) =>
                  onChangeAdvancedFilters?.({
                    ...advancedFilters,
                    entryOrigins,
                  })
                }
              />
            </div>

            <div className={styles.advancedFiltersSection}>
              <span className={styles.advancedFiltersLabel}>Assigned To</span>
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select users"
                className={styles.advancedFiltersSelect}
                // popupMatchSelectWidth={false}
                value={advancedFilters.assignedTo}
                options={assignedFilterOptions}
                optionFilterProp="label"
                optionLabelProp="tagLabel"
                maxTagCount={1}
                onChange={(nextAssignedTo) =>
                  onChangeAdvancedFilters?.({
                    ...advancedFilters,
                    assignedTo: nextAssignedTo,
                  })
                }
              />
            </div>

            <div className={styles.advancedFiltersFooter}>
              <Button
                type="link"
                className={styles.advancedFiltersClear}
                onClick={() =>
                  onChangeAdvancedFilters?.({
                    statuses: [],
                    assignedTo: [],
                    entryOrigins: [],
                  })
                }
              >
                Clear
              </Button>
            </div>
          </div>
        }
      >
        <Button
          className={`${styles.antBtn} ${
            advancedFilters.statuses.length ||
            advancedFilters.assignedTo.length ||
            advancedFilters.entryOrigins.length
              ? styles.quickFilterButtonActive
              : ""
          }`}
        >
          Advanced Filters
          <DownOutlined style={{ fontSize: "14px" }} />
        </Button>
      </Popover>
      {showMyAccessibleToggle && accessLevel < 2 && (
        <Button
          className={`${styles.antBtn} ${
            isMyAccessibleOnly ? styles.quickFilterButtonActive : ""
          }`}
          onClick={onToggleMyAccessibleOnly}
        >
          My Accessible Items
        </Button>
      )}
      {activeFilterCount > 0 && (
        <div className={styles.selectionBadge}>
          <FilterFilled />
          {activeFilterCount} filter
          {activeFilterCount !== 1 ? "s" : ""} active
          <button
            type="button"
            className={styles.selectionBadgeClear}
            onClick={onClearFilters}
            title="Clear filters"
          >
            ✕
          </button>
        </div>
      )}
    </div>
    <div className="d-flex align-items-center gap-2 p-1">
      {selectedCount === 0 && (
        <div className={styles.totalTrackingMaterialsWrap}>
          <div className={styles.totalTrackingMaterials}>
            {submittalsTotalCount} Total Tracking Materials
          </div>
        </div>
      )}
      {selectedCount > 0 && (
        <>
          <div className={styles.selectionBadge}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect
                x="0.5"
                y="0.5"
                width="13"
                height="13"
                rx="3.5"
                fill="#3b82f6"
                stroke="#3b82f6"
              />
              <path
                d="M3 7L5.5 9.5L11 4"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
            <button
              type="button"
              className={styles.selectionBadgeClear}
              onClick={onClearSelection}
              title="Clear selection"
            >
              ✕
            </button>
          </div>

          <Dropdown
            menu={bulkMenuProps}
            placement="bottomLeft"
            trigger={["click"]}
          >
            <Button className={styles.antBtn}>
              Bulk Actions
              <DownOutlined style={{ fontSize: "14px" }} />
            </Button>
          </Dropdown>

          {selectedCount === 1 && isSubscribedToWarehouse && (
            <Tooltip
              title={
                canCreatePurchaseOrder
                  ? ""
                  : "You do not have permission to create a purchase order."
              }
            >
              <Button
                className={`${styles.antBtn} ${styles.procoreActionButton}`}
                disabled={!canCreatePurchaseOrder}
                onClick={onOpenCreatePurchaseOrder}
              >
                <img src={ProcoreLogo} alt="Procore" width={16} height={16} />
                Create Purchase Order
              </Button>
            </Tooltip>
          )}
        </>
      )}
      <SyncStatusIndicator
        syncState={syncState}
        syncMessage={syncMessage}
        onRetrySync={onRetrySync}
      />

      {/* <Button shape="round" type="primary" size="large">
        Save Changes
      </Button> */}
    </div>
  </div>
);

const WARNING_COLUMNS = [
  "submittalIssued",
  "submittalReturned",
  "contractIssuedRelease",
  "projectedDeliveryDate",
  "actualDeliveryDate",
  "subCommittedDeliveryDate",
];
const SOCKET_EDIT_BATCH_WINDOW_MS = 40;
const SOCKET_IO_CLIENT_URL = "https://cdn.socket.io/4.6.0/socket.io.min.js";
const EDITING_PRESENCE_THEMES = [
  { color: "#f56a00", backgroundColor: "#fff4e8" },
  { color: "#7265e6", backgroundColor: "#f3f0ff" },
  { color: "#00a2ae", backgroundColor: "#e8fbfc" },
  { color: "#e6475a", backgroundColor: "#fff0f2" },
  { color: "#43a047", backgroundColor: "#edf9ef" },
];

const NON_PERSISTED_ROW_KEYS = new Set([
  "id",
  "rowType",
  "level",
  "treeDepth",
  "treeLines",
  "isLastSibling",
  "hasChildren",
  "displayTitle",
  "summaryCounts",
  "isExpanded",
  "groupKey",
  "divisionDisplayTitle",
  "__rowId",
  "__sourceIndex",
  "tempId",
  "scheduleSyncWarning",
  "projectedDeliveryDateStatus",
  "actualDeliveryDateStatus",
  "subCommittedDeliveryDateStatus",
  "isEdited",
]);

const SUBMITTAL_DROPDOWN_COPYABLE_KEYS = new Set([
  "specNumber",
  "specRevision",
  "submittalReturned",
  "submittalIssued",
  "specType",
  "specResponse",
  "responsibleContractor",
]);

const SOCKET_ROW_META_KEYS = new Set([
  "entryID",
  "entryId",
  "rowID",
  "rowId",
  "id",
  "tempId",
  "companyID",
  "companyId",
  "company",
  "projectID",
  "projectId",
  "project",
  "actor",
  "email",
  "loginEmail",
  "userEmail",
  "isNew",
]);

const CLONED_ROW_NON_COPYABLE_KEYS = new Set([
  ...NON_PERSISTED_ROW_KEYS,
  ...SOCKET_ROW_META_KEYS,
  "entryID",
  "entryId",
  "order_index",
  "koncurentStatus",
  "entryOrigin",
  "parentID",
  "parentId",
]);

const TASK_ASSIGNMENT_USER_INFO_KEYS = [
  "accessLevel",
  "warehouseSubscription",
  "wareHouseAccessLevel",
  "leadTimeGranularPermission",
  "subCommittedDeliveryDateGranularPermission",
  "actualDeliveryDateGranularPermission",
  "viewOnlyAssignedEntriesGranularPermission",
  "greyOutSubRequiredDateGranularPermission",
  "warningStyle",
];

// Normalizes edited values into the format expected by the save API.
function normalizePersistedFieldValue(key, value, alignedData) {
  if (value === null || value === undefined) {
    return "";
  }

  if (alignedData?.[key]?.type === "date") {
    const parsedDate = parseFormulaDate(value);
    return parsedDate ? parsedDate.format(FORMULA_DATE_FORMAT) : "";
  }

  return value;
}

// Builds a row update object that only includes fields changed by the edit.
function buildChangedRowUpdate(previousRow, nextRow, alignedData) {
  const entryID = firstFilled(nextRow?.entryID);

  if (
    !entryID ||
    nextRow?.isNew === "true" ||
    String(entryID).startsWith("new-")
  ) {
    return null;
  }

  const changedRowUpdate = { entryID };
  let hasChanges = false;
  const keys = new Set([
    ...Object.keys(previousRow || {}),
    ...Object.keys(nextRow || {}),
  ]);

  keys.forEach((key) => {
    if (key === "entryID" || NON_PERSISTED_ROW_KEYS.has(key)) {
      return;
    }

    const previousValue = normalizePersistedFieldValue(
      key,
      previousRow?.[key],
      alignedData,
    );
    const nextValue = normalizePersistedFieldValue(
      key,
      nextRow?.[key],
      alignedData,
    );

    if (previousValue !== nextValue) {
      changedRowUpdate[key] = nextValue;
      hasChanges = true;
    }
  });

  return hasChanges ? changedRowUpdate : null;
}

// Copies only the fields that should follow a submittal-number selection.
function buildSubmittalsDropDownRowPatch(sourceRow) {
  if (!sourceRow || typeof sourceRow !== "object") {
    return {};
  }

  return Object.entries(sourceRow).reduce((rowPatch, [key, value]) => {
    if (
      !SUBMITTAL_DROPDOWN_COPYABLE_KEYS.has(key) ||
      typeof value === "object"
    ) {
      return rowPatch;
    }

    rowPatch[key] = value ?? "";
    return rowPatch;
  }, {});
}

// Builds the persisted field payload for a duplicate while preserving the new row's identity.
function buildClonedRowPatch(sourceRow, alignedData, cloneParentId = "") {
  if (!sourceRow || typeof sourceRow !== "object") {
    return {};
  }

  const clonedRowPatch = Object.entries(sourceRow).reduce(
    (rowPatch, [key, value]) => {
      if (CLONED_ROW_NON_COPYABLE_KEYS.has(key) || typeof value === "object") {
        return rowPatch;
      }

      rowPatch[key] = normalizePersistedFieldValue(key, value, alignedData);
      return rowPatch;
    },
    {},
  );

  if (cloneParentId) {
    clonedRowPatch.parentID = cloneParentId;
  }

  return clonedRowPatch;
}

// Uses the app's shared backend base URL so sockets point to the same environment as normal API requests.
function getProcurementSocketBackendUrl() {
  return String(ApiUrl || "").replace(/\/+$/, "");
}

// Loads the Socket.IO browser client on demand without adding a new package dependency.
function loadSocketIoClientScript() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Socket client can only be loaded in the browser."),
    );
  }

  if (typeof window.io === "function") {
    return Promise.resolve(window.io);
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[data-procurement-socket-client="true"]',
    );

    const handleLoad = () => {
      if (typeof window.io === "function") {
        resolve(window.io);
        return;
      }

      reject(new Error("Socket.IO client loaded without exposing window.io."));
    };

    const handleError = () => {
      reject(new Error("Failed to load the Socket.IO client script."));
    };

    if (existingScript) {
      if (
        existingScript.dataset.loaded === "true" &&
        typeof window.io === "function"
      ) {
        resolve(window.io);
        return;
      }

      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = SOCKET_IO_CLIENT_URL;
    script.async = true;
    script.dataset.procurementSocketClient = "true";
    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "true";
        handleLoad();
      },
      { once: true },
    );
    script.addEventListener("error", handleError, { once: true });
    document.body.appendChild(script);
  });
}

// Builds a stable signature for a just-created draft so a later insert socket can claim the matching temp row.
function buildPendingInsertSignature(row) {
  return JSON.stringify({
    divisionID: normalizeText(
      firstFilled(row?.divisionID, row?.divisionId, row?.div, row?.division),
    ),
    subdivisionID: normalizeText(
      firstFilled(
        row?.subdivisionID,
        row?.subdivisionId,
        row?.subDiv,
        row?.subdivision,
        row?.specSection,
      ),
    ),
    specSection: normalizeText(firstFilled(row?.specSection)),
    specNumber: normalizeText(firstFilled(row?.specNumber)),
    specTitle: normalizeText(firstFilled(row?.specTitle)),
    specRevision: normalizeText(firstFilled(row?.specRevision)),
    submittalPrepTime: String(firstFilled(row?.submittalPrepTime, "")),
    approvalTime: String(firstFilled(row?.approvalTime, "")),
    leadTime: String(firstFilled(row?.leadTime, "")),
    entryOrigin: normalizeText(firstFilled(row?.entryOrigin)),
  });
}

// Merges a socket-delivered inserted row into a local draft while preserving any values the user already typed.
function mergeSocketInsertedRow(existingRow, incomingRow, incomingEntryId) {
  const mergedRow = { ...incomingRow };

  Object.keys(existingRow || {}).forEach((key) => {
    const localValue = existingRow?.[key];

    if (
      localValue !== null &&
      localValue !== undefined &&
      !(typeof localValue === "string" && localValue.trim() === "")
    ) {
      mergedRow[key] = localValue;
    }
  });

  mergedRow.entryID = incomingEntryId;
  mergedRow.tempId = incomingEntryId;
  mergedRow.id = `submittal:${incomingEntryId}`;
  mergedRow.isNew = "false";

  return mergedRow;
}

// Shapes socket-delivered procurement rows so they can be inserted back into the grid consistently.
function buildProcurementSocketBaseRow(incomingRow, fallbackEntryId = "") {
  const resolvedEntryId = firstFilled(
    fallbackEntryId,
    incomingRow?.entryID,
    incomingRow?.newEntryID,
    incomingRow?.entryId,
    incomingRow?.rowID,
    incomingRow?.rowId,
    incomingRow?.id,
  );

  if (!resolvedEntryId) {
    return null;
  }

  return {
    ...(incomingRow || {}),
    entryID: resolvedEntryId,
    tempId: resolvedEntryId,
    id: `submittal:${resolvedEntryId}`,
    isNew: "false",
  };
}

// Extracts the actor email from procurement socket messages so local echo events can be ignored safely.
function getProcurementSocketActorEmail(message) {
  return normalizeText(
    firstFilled(
      message?.actor,
      message?.email,
      message?.loginEmail,
      message?.userEmail,
      message?.user?.email,
      message?.data?.actor,
      message?.data?.email,
      message?.data?.loginEmail,
      message?.data?.userEmail,
      message?.data?.user?.email,
    ),
  ).toLowerCase();
}

// Normalizes socket event payloads that may arrive as a single object or a one-item args array.
function getProcurementSocketEnvelope(message) {
  if (Array.isArray(message)) {
    return message[0] || {};
  }

  return message || {};
}

function buildProcurementEditingCellKey(entryId, columnName) {
  const normalizedEntryId = normalizeText(firstFilled(entryId));
  const normalizedColumnName = normalizeText(firstFilled(columnName));

  if (!normalizedEntryId || !normalizedColumnName) {
    return "";
  }

  return `${normalizedEntryId}::${normalizedColumnName}`;
}

function getProcurementSocketCellLocation(message) {
  const normalizedMessage = getProcurementSocketEnvelope(message);
  const candidates = [
    normalizedMessage?.cellLocation,
    normalizedMessage?.data?.cellLocation,
    normalizedMessage?.data,
  ];

  for (const candidate of candidates) {
    if (
      !candidate ||
      typeof candidate !== "object" ||
      Array.isArray(candidate)
    ) {
      continue;
    }

    const entryID = firstFilled(
      candidate?.entryID,
      candidate?.entryId,
      candidate?.rowID,
      candidate?.rowId,
      candidate?.id,
    );
    const columnName = firstFilled(candidate?.columnName, candidate?.field);

    if (entryID && columnName) {
      return { entryID: String(entryID), columnName: String(columnName) };
    }
  }

  return null;
}

function formatProcurementEditingUserLabel(actorEmail, userDirectory, message) {
  const normalizeEditingUserLabel = (value) =>
    String(firstFilled(value, ""))
      .trim()
      .toLowerCase()
      .replace(
        /(^|[\s-])([a-z])/g,
        (match, prefix, character) => `${prefix}${character.toUpperCase()}`,
      );

  const normalizedActorEmail = normalizeText(actorEmail).toLowerCase();
  const explicitLabel = firstFilled(
    message?.firstLastName,
    message?.actorName,
    message?.fullName,
    message?.userName,
    message?.user?.fullName,
    message?.cellLocation?.firstLastName,
    message?.data?.firstLastName,
    message?.data?.actorName,
    message?.data?.fullName,
    message?.data?.userName,
    message?.data?.cellLocation?.firstLastName,
    message?.data?.user?.fullName,
  );

  if (explicitLabel) {
    return normalizeEditingUserLabel(explicitLabel);
  }

  if (normalizedActorEmail && userDirectory?.[normalizedActorEmail]) {
    return normalizeEditingUserLabel(userDirectory[normalizedActorEmail]);
  }

  const emailLocalPart =
    normalizedActorEmail.split("@")[0] || normalizedActorEmail;
  return emailLocalPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function getProcurementEditingPresenceTheme(actorEmail) {
  const normalizedActorEmail = normalizeText(actorEmail).toLowerCase();

  if (!normalizedActorEmail) {
    return EDITING_PRESENCE_THEMES[0];
  }

  const hash = normalizedActorEmail
    .split("")
    .reduce(
      (accumulator, character) => accumulator + character.charCodeAt(0),
      0,
    );

  return EDITING_PRESENCE_THEMES[hash % EDITING_PRESENCE_THEMES.length];
}

function buildProcurementEditingUserDirectory(tasksDict, usersInfo) {
  const directory = {};

  Object.entries(tasksDict || {}).forEach(([email, value]) => {
    const normalizedEmail = normalizeText(email).toLowerCase();
    const fullName = firstFilled(value?.fullName, value?.name);

    if (normalizedEmail && fullName) {
      directory[normalizedEmail] = fullName;
    }
  });

  const normalizedUsersInfo = Array.isArray(usersInfo)
    ? usersInfo
    : usersInfo
      ? [usersInfo]
      : [];

  normalizedUsersInfo.forEach((userInfo) => {
    const normalizedEmail = normalizeText(
      firstFilled(
        userInfo?.email,
        userInfo?.loginEmail,
        userInfo?.["Email Address"],
      ),
    ).toLowerCase();
    const fullName = firstFilled(
      userInfo?.fullName,
      userInfo?.name,
      `${firstFilled(userInfo?.firstName, userInfo?.["First Name"])} ${firstFilled(
        userInfo?.lastName,
        userInfo?.["Last Name"],
      )}`.trim(),
    );

    if (normalizedEmail && fullName && !directory[normalizedEmail]) {
      directory[normalizedEmail] = fullName;
    }
  });

  return directory;
}

// Reads the project identifier from either a socket envelope or an individual row payload.
function getProcurementSocketProjectId(source) {
  return firstFilled(source?.projectID, source?.projectId, source?.project);
}

// Reads the company identifier from either a socket envelope or an individual row payload.
function getProcurementSocketCompanyId(source) {
  return firstFilled(source?.companyID, source?.companyId, source?.company);
}

// Confirms a socket payload belongs to the project currently open in the grid.
function doesProcurementSocketMatchCurrentProject(
  message,
  row,
  companyId,
  projectId,
) {
  const incomingProjectId = firstFilled(
    getProcurementSocketProjectId(row),
    getProcurementSocketProjectId(message),
  );
  const incomingCompanyId = firstFilled(
    getProcurementSocketCompanyId(row),
    getProcurementSocketCompanyId(message),
  );

  if (incomingProjectId && String(incomingProjectId) !== String(projectId)) {
    return false;
  }

  if (incomingCompanyId && String(incomingCompanyId) !== String(companyId)) {
    return false;
  }

  return true;
}

// Collects deleted entry IDs from the different payload shapes the backend may emit.
function collectProcurementDeletedEntryIds(value, accumulator) {
  if (value === null || value === undefined || value === "") {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) =>
      collectProcurementDeletedEntryIds(item, accumulator),
    );
    return;
  }

  if (typeof value === "object") {
    collectProcurementDeletedEntryIds(value?.entryID, accumulator);
    collectProcurementDeletedEntryIds(value?.entryId, accumulator);
    collectProcurementDeletedEntryIds(value?.deletedEntryID, accumulator);
    collectProcurementDeletedEntryIds(value?.deletedEntryId, accumulator);
    collectProcurementDeletedEntryIds(value?.hiddenEntryID, accumulator);
    collectProcurementDeletedEntryIds(value?.hiddenEntryId, accumulator);
    collectProcurementDeletedEntryIds(value?.rowID, accumulator);
    collectProcurementDeletedEntryIds(value?.rowId, accumulator);
    collectProcurementDeletedEntryIds(value?.id, accumulator);
    collectProcurementDeletedEntryIds(value?.entryIDs, accumulator);
    collectProcurementDeletedEntryIds(value?.entryIds, accumulator);
    collectProcurementDeletedEntryIds(value?.deletedEntryIDs, accumulator);
    collectProcurementDeletedEntryIds(value?.deletedEntryIds, accumulator);
    collectProcurementDeletedEntryIds(value?.hiddenEntryIDs, accumulator);
    collectProcurementDeletedEntryIds(value?.hiddenEntryIds, accumulator);
    collectProcurementDeletedEntryIds(value?.rows, accumulator);
    return;
  }

  const normalizedValue = normalizeText(value);
  if (normalizedValue) {
    accumulator.add(normalizedValue);
  }
}

// Collects entry IDs from task-assignment socket payloads, including nested taskIDsAssigned arrays.
function collectProcurementTaskEntryIds(value, accumulator) {
  if (value === null || value === undefined || value === "") {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectProcurementTaskEntryIds(item, accumulator));
    return;
  }

  if (typeof value === "object") {
    collectProcurementTaskEntryIds(value?.taskID, accumulator);
    collectProcurementTaskEntryIds(value?.taskId, accumulator);
    collectProcurementTaskEntryIds(value?.entryID, accumulator);
    collectProcurementTaskEntryIds(value?.entryId, accumulator);
    collectProcurementTaskEntryIds(value?.entryIDs, accumulator);
    collectProcurementTaskEntryIds(value?.entryIds, accumulator);
    collectProcurementTaskEntryIds(value?.taskIDsAssigned, accumulator);
    collectProcurementTaskEntryIds(value?.rows, accumulator);
    return;
  }

  const normalizedValue = normalizeText(value);
  if (normalizedValue) {
    accumulator.add(normalizedValue);
  }
}

// Reads all assigned entry IDs from task-assigned / task-deleted payloads without assuming one strict backend shape.
function getProcurementSocketTaskEntryIds(message) {
  const taskEntryIds = new Set();

  collectProcurementTaskEntryIds(message?.taskID, taskEntryIds);
  collectProcurementTaskEntryIds(message?.taskId, taskEntryIds);
  collectProcurementTaskEntryIds(message?.entryID, taskEntryIds);
  collectProcurementTaskEntryIds(message?.entryId, taskEntryIds);
  collectProcurementTaskEntryIds(message?.entryIDs, taskEntryIds);
  collectProcurementTaskEntryIds(message?.entryIds, taskEntryIds);
  collectProcurementTaskEntryIds(message?.taskIDsAssigned, taskEntryIds);
  collectProcurementTaskEntryIds(message?.rows, taskEntryIds);
  collectProcurementTaskEntryIds(message?.data, taskEntryIds);

  return [...taskEntryIds];
}

function collectProcurementSocketEmails(value, accumulator) {
  if (value === null || value === undefined || value === "") {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectProcurementSocketEmails(item, accumulator));
    return;
  }

  if (typeof value === "object") {
    collectProcurementSocketEmails(value?.contractorEmails, accumulator);
    collectProcurementSocketEmails(value?.assignedEmail, accumulator);
    collectProcurementSocketEmails(value?.assignedToEmail, accumulator);
    collectProcurementSocketEmails(value?.assigneeEmail, accumulator);
    collectProcurementSocketEmails(value?.email, accumulator);
    return;
  }

  const normalizedEmail = normalizeText(value).toLowerCase();

  if (normalizedEmail) {
    accumulator.add(normalizedEmail);
  }
}

// Resolves all assignee emails from task-related socket payloads when the backend includes them.
function getProcurementSocketAssignedEmails(message) {
  const firstSocketRow =
    Array.isArray(message?.data) && message.data.length > 0
      ? message.data[0]
      : null;
  const assignedEmails = new Set();

  collectProcurementSocketEmails(message?.contractorEmails, assignedEmails);
  collectProcurementSocketEmails(message?.assignedEmail, assignedEmails);
  collectProcurementSocketEmails(message?.assignedToEmail, assignedEmails);
  collectProcurementSocketEmails(message?.assigneeEmail, assignedEmails);
  collectProcurementSocketEmails(message?.email, assignedEmails);
  collectProcurementSocketEmails(
    firstSocketRow?.contractorEmails,
    assignedEmails,
  );
  collectProcurementSocketEmails(firstSocketRow?.assignedEmail, assignedEmails);
  collectProcurementSocketEmails(
    firstSocketRow?.assignedToEmail,
    assignedEmails,
  );
  collectProcurementSocketEmails(firstSocketRow?.assigneeEmail, assignedEmails);
  collectProcurementSocketEmails(firstSocketRow?.email, assignedEmails);

  return [...assignedEmails];
}

// Extracts row-like task socket payloads whether the backend sends them at the top level or nested under data.
function getProcurementSocketTaskRows(message, socketMessage) {
  const candidateRows = Array.isArray(message)
    ? message
    : Array.isArray(socketMessage?.data)
      ? socketMessage.data
      : socketMessage?.data && typeof socketMessage.data === "object"
        ? [socketMessage.data]
        : socketMessage && typeof socketMessage === "object"
          ? [socketMessage]
          : [];

  return candidateRows.filter(
    (row) => row && typeof row === "object" && !Array.isArray(row),
  );
}

function collectProcurementPendingApprovalEntryIds(value, accumulator) {
  if (value === null || value === undefined || value === "") {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) =>
      collectProcurementPendingApprovalEntryIds(item, accumulator),
    );
    return;
  }

  if (typeof value === "object") {
    collectProcurementPendingApprovalEntryIds(value?.entryID, accumulator);
    collectProcurementPendingApprovalEntryIds(value?.entryId, accumulator);
    collectProcurementPendingApprovalEntryIds(value?.taskID, accumulator);
    collectProcurementPendingApprovalEntryIds(value?.taskId, accumulator);
    collectProcurementPendingApprovalEntryIds(value?.data, accumulator);
    return;
  }

  const normalizedValue = normalizeText(value);
  if (normalizedValue) {
    accumulator.add(normalizedValue);
  }
}

function getProcurementSocketPendingApprovalEntryIds(message) {
  const entryIds = new Set();

  collectProcurementPendingApprovalEntryIds(message?.entryID, entryIds);
  collectProcurementPendingApprovalEntryIds(message?.entryId, entryIds);
  collectProcurementPendingApprovalEntryIds(message?.data, entryIds);

  return [...entryIds];
}

// Reads all deleted entry IDs from a socket delete message without assuming one strict backend payload shape.
function getProcurementSocketDeletedEntryIds(message) {
  const deletedEntryIds = new Set();

  collectProcurementDeletedEntryIds(message?.entryID, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.entryId, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.deletedEntryID, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.deletedEntryId, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.deletedEntryIDs, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.deletedEntryIds, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.hiddenEntryID, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.hiddenEntryId, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.hiddenEntryIDs, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.hiddenEntryIds, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.entryIDs, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.entryIds, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.rows, deletedEntryIds);
  collectProcurementDeletedEntryIds(message?.data, deletedEntryIds);

  return [...deletedEntryIds];
}

// Extracts the stable procurement entry identifier from socket row payloads.
function getProcurementSocketEntryId(row) {
  return String(
    firstFilled(row?.entryID, row?.entryId, row?.rowID, row?.rowId, row?.id),
  );
}

// Detects whether a socket row explicitly includes family-linkage fields, even when clearing them to detach a submittal.
function hasProcurementSocketParentReference(row) {
  return Boolean(
    row &&
    (Object.prototype.hasOwnProperty.call(row, "parentID") ||
      Object.prototype.hasOwnProperty.call(row, "parentId")),
  );
}

// Builds the subset of incoming socket fields that actually changed on a row.
function buildProcurementSocketRowPatch(existingRow, incomingRow, alignedData) {
  const rowPatch = {};
  const hasIncomingParentReference =
    hasProcurementSocketParentReference(incomingRow);

  if (hasIncomingParentReference) {
    const previousParentReference = firstFilled(
      existingRow?.parentID,
      existingRow?.parentId,
    );
    const nextParentReference = normalizeText(
      Object.prototype.hasOwnProperty.call(incomingRow || {}, "parentID")
        ? incomingRow?.parentID
        : incomingRow?.parentId,
    );

    if (previousParentReference !== nextParentReference) {
      rowPatch.parentID = nextParentReference;
      rowPatch.parentId = nextParentReference;
    }
  }

  Object.keys(incomingRow || {}).forEach((key) => {
    if (key === "parentID" || key === "parentId") {
      return;
    }

    if (SOCKET_ROW_META_KEYS.has(key) || NON_PERSISTED_ROW_KEYS.has(key)) {
      return;
    }

    const previousValue = normalizePersistedFieldValue(
      key,
      existingRow?.[key],
      alignedData,
    );
    const nextValue = normalizePersistedFieldValue(
      key,
      incomingRow?.[key],
      alignedData,
    );

    if (previousValue !== nextValue) {
      rowPatch[key] = incomingRow[key];
    }
  });

  return rowPatch;
}

// Resolves the stable local identifier used for updates, selection, and persistence diffs.
function getProcurementRowId(row, index) {
  return firstFilled(row?.entryID, row?.tempId, row?.id, `row-${index}`);
}

// Applies one or more row patches, recalculates affected values, and builds autosave updates.
function applyLocalRowPatches({
  previousRows,
  rowPatches,
  formulaColumns,
  alignedData,
  scheduleSyncContext,
  settingsInfo,
  overwriteCalculations,
  businessCalendarContext,
  markEdited = true,
}) {
  const nextRows = previousRows.map((row, index) => {
    const rowId = getProcurementRowId(row, index);
    const rowPatch = rowPatches.get(rowId);

    if (!rowPatch) {
      return row;
    }

    const scheduleAwareRowPatch = applyScheduleIdClearingPatch(rowPatch);

    return {
      ...row,
      ...(markEdited ? { isEdited: true } : {}),
      ...(Object.prototype.hasOwnProperty.call(
        rowPatch,
        "dateRequiredOnSite",
      ) && !Object.prototype.hasOwnProperty.call(rowPatch, "scheduleID")
        ? { scheduleID: "" }
        : {}),
      ...scheduleAwareRowPatch,
    };
  });

  const shouldRecalculateAllRows =
    overwriteCalculations || rowPatches.size !== 1;

  const recalculatedRows = shouldRecalculateAllRows
    ? applyFormulaColumnsToRows(
        nextRows,
        formulaColumns,
        alignedData,
        scheduleSyncContext,
        settingsInfo,
        businessCalendarContext,
      )
    : nextRows.map((row, index) => {
        const rowId = getProcurementRowId(row, index);

        if (!rowPatches.has(rowId)) {
          return row;
        }

        return applyFormulaColumnsToRow(
          row,
          formulaColumns,
          alignedData,
          scheduleSyncContext,
          businessCalendarContext,
        );
      });

  const previousRowsById = new Map(
    previousRows.map((row, index) => [getProcurementRowId(row, index), row]),
  );

  const changedRowUpdates = recalculatedRows
    .map((row, index) =>
      buildChangedRowUpdate(
        previousRowsById.get(getProcurementRowId(row, index)),
        row,
        alignedData,
      ),
    )
    .filter(Boolean);

  return { recalculatedRows, changedRowUpdates };
}

// Coordinates procurement log data, grid behavior, calculations, and autosave.
function ProcurementLogPage() {
  const { company_id, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const procurementSocketRef = useRef(null);
  const handleSocketInsertedRowRef = useRef(null);
  const handleSocketDeletedRowRef = useRef(null);
  const handleSocketRestoredDeletedRowRef = useRef(null);
  const handleSocketCriticalPathRowRef = useRef(null);
  const handleSocketTaskAssignmentRef = useRef(null);
  const handleSocketEditedRowRef = useRef(null);
  const handleSocketPostToProcoreRef = useRef(null);
  const handleSocketPendingApprovalRef = useRef(null);
  const handleSocketApprovalDecisionRef = useRef(null);
  const handleSocketUserInCellRef = useRef(null);
  const handleSocketUserOutOfCellRef = useRef(null);
  const activeSyncOperationsRef = useRef(new Map());
  const nextSyncOperationIdRef = useRef(1);
  const syncStatusEpochRef = useRef(0);
  const syncIncomingTimeoutRef = useRef(null);
  const pendingCellFlashRef = useRef([]);
  const flashFlushScheduledRef = useRef(false);
  const pendingSocketEditRowsRef = useRef(new Map());
  const pendingSocketEditFlushTimeoutRef = useRef(null);
  const manualSyncRetryPendingRef = useRef(false);
  const gridRowsDataRef = useRef([]);
  const appliedRowsDataRef = useRef(null);
  const pendingInsertedDraftsRef = useRef([]);
  const removedProcurementRowsRef = useRef(new Map());
  const remoteEditingCellByActorRef = useRef(new Map());
  const editingPresenceByCellKeyRef = useRef({});
  const localEditingCellRef = useRef(null);
  const savedColumnStateRef = useRef(null);
  const selectedIdsRef = useRef(new Set());
  const criticalPathRequestIdsRef = useRef(new Set());
  const rowActionRequestIdsRef = useRef(new Set());
  const preFilteredExpandedDivisionsRef = useRef(null);
  const preFilteredExpandedSubdivisionsRef = useRef(null);
  const wasTreePrunedRef = useRef(false);
  const [expandedDivisions, setExpandedDivisions] = useState({});
  const [expandedSubdivisions, setExpandedSubdivisions] = useState({});
  const [gridRowsData, setGridRowsData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [showAccessibleOnly, setShowAccessibleOnly] = useState(false);
  const [sortModel, setSortModel] = useState(null);
  const [selectedView, setSelectedView] = useState(undefined);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [cloningEntryIds, setCloningEntryIds] = useState(() => new Set());
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [isPostToProcoreVisible, setIsPostToProcoreVisible] = useState(false);
  const [isAssignDrawerOpen, setIsAssignDrawerOpen] = useState(false);
  const [assignDrawerRowId, setAssignDrawerRowId] = useState("");
  const [assignDrawerSnapshotRow, setAssignDrawerSnapshotRow] = useState(null);
  const [isUploadScheduleModalOpen, setIsUploadScheduleModalOpen] =
    useState(false);
  const [columnFilterModel, setColumnFilterModel] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState({
    statuses: [],
    assignedTo: [],
    entryOrigins: [],
  });
  const [syncStatus, setSyncStatus] = useState(DEFAULT_SYNC_STATUS);
  const [socketSyncErrorMessage, setSocketSyncErrorMessage] = useState("");
  const [liveUsersInfo, setLiveUsersInfo] = useState([]);
  const [liveTasksDict, setLiveTasksDict] = useState({});
  const [liveItemsPendingApprovals, setLiveItemsPendingApprovals] = useState(
    [],
  );
  const [deletedEntryIds, setDeletedEntryIds] = useState([]);
  const [editingPresenceByCellKey, setEditingPresenceByCellKey] = useState({});
  const [dependencyGraph, setDependencyGraph] = useState({});
  const [isUploadProcurementLogModalOpen, setIsUploadProcurementLogModalOpen] =
    useState(false);
  const [itemsShippedData, setItemsShippedData] = useState([]);
  const [itemsShippedModal, setItemsShippedModal] = useState(false);
  const [itemsShippedHistoryData, setItemsShippedHistoryData] = useState([]);
  const [isitemsShippedLoading, setIsitemsShippedLoading] = useState(true);

  const {
    isLoading,
    alignedData,
    rowsData,
    divisions,
    subdivisions,
    schedule,
    workingDays,
    nonWorkingDays,
    usersInfo: baseUsersInfo,
    accessLevel,
    wareHouseAccessLevel,
    warehouseSubscription,
    settingsInfo,
    lastUsedView,
    viewNames,
    itemsPendingApprovals: baseItemsPendingApprovals,
    tasksDict: baseTasksDict,
    dependenciesDict,
    projectName,
    projectAddress,
    approvalTime,
    reviewTime,
    submittalsDropDown,
    error: loadError,
    reloadData,
  } = useProcurementData(company_id, id);

  useEffect(() => {
    setDependencyGraph(dependenciesDict || {});
  }, [dependenciesDict]);

  useEffect(() => {
    setLiveUsersInfo(baseUsersInfo ?? []);
  }, [baseUsersInfo]);

  useEffect(() => {
    setLiveTasksDict(baseTasksDict ?? {});
  }, [baseTasksDict]);

  useEffect(() => {
    setLiveItemsPendingApprovals(
      (baseItemsPendingApprovals || []).map((entryId) => String(entryId)),
    );
  }, [baseItemsPendingApprovals]);

  useEffect(() => {
    setDeletedEntryIds([]);
  }, [company_id, id, rowsData]);

  const clearIncomingSyncTimeout = useCallback(() => {
    if (
      syncIncomingTimeoutRef.current !== null &&
      typeof window !== "undefined"
    ) {
      window.clearTimeout(syncIncomingTimeoutRef.current);
    }

    syncIncomingTimeoutRef.current = null;
  }, []);

  const resolveIdleSyncStatus = useCallback(
    (message = DEFAULT_SYNC_STATUS.message) =>
      socketSyncErrorMessage
        ? {
            state: "error",
            message: socketSyncErrorMessage,
          }
        : {
            state: "synced",
            message: firstFilled(message, DEFAULT_SYNC_STATUS.message),
          },
    [socketSyncErrorMessage],
  );

  const setSocketSyncErrorState = useCallback((message) => {
    const nextMessage = firstFilled(message, DEFAULT_SOCKET_SYNC_ERROR_MESSAGE);
    setSocketSyncErrorMessage(nextMessage);

    if (activeSyncOperationsRef.current.size > 0) {
      return;
    }

    setSyncStatus({
      state: "error",
      message: nextMessage,
    });
  }, []);

  const clearSocketSyncErrorState = useCallback(() => {
    setSocketSyncErrorMessage("");

    if (activeSyncOperationsRef.current.size > 0) {
      return;
    }

    setSyncStatus(DEFAULT_SYNC_STATUS);
  }, []);

  const beginSyncActivity = useCallback(
    (message) => {
      clearIncomingSyncTimeout();

      const token = {
        id: nextSyncOperationIdRef.current++,
        epoch: syncStatusEpochRef.current,
      };

      activeSyncOperationsRef.current.set(token.id, {
        epoch: token.epoch,
        message: firstFilled(message, "Saving changes"),
      });

      setSyncStatus({
        state: "syncing",
        message: firstFilled(message, "Saving changes"),
      });

      return token;
    },
    [clearIncomingSyncTimeout],
  );

  const finishSyncActivity = useCallback(
    (token, message) => {
      if (!token) {
        return;
      }

      activeSyncOperationsRef.current.delete(token.id);

      if (token.epoch !== syncStatusEpochRef.current) {
        return;
      }

      const activeOperations = [
        ...activeSyncOperationsRef.current.values(),
      ].filter((operation) => operation.epoch === syncStatusEpochRef.current);

      if (activeOperations.length) {
        const latestOperation = activeOperations[activeOperations.length - 1];
        setSyncStatus({
          state: "syncing",
          message: latestOperation?.message || "Saving changes",
        });
        return;
      }

      setSyncStatus(resolveIdleSyncStatus(message));
    },
    [resolveIdleSyncStatus],
  );

  const failSyncActivity = useCallback(
    (token, message) => {
      if (token) {
        activeSyncOperationsRef.current.delete(token.id);
      }

      syncStatusEpochRef.current += 1;
      activeSyncOperationsRef.current.clear();
      clearIncomingSyncTimeout();

      setSyncStatus({
        state: "error",
        message: firstFilled(message, DEFAULT_SYNC_ERROR_MESSAGE),
      });
    },
    [clearIncomingSyncTimeout],
  );

  const pulseIncomingSyncActivity = useCallback(
    (message = "Receiving live updates") => {
      if (activeSyncOperationsRef.current.size > 0) {
        return;
      }

      clearIncomingSyncTimeout();

      const pulseEpoch = syncStatusEpochRef.current;
      setSyncStatus({
        state: "syncing",
        message,
      });

      if (typeof window === "undefined") {
        return;
      }

      syncIncomingTimeoutRef.current = window.setTimeout(() => {
        if (
          activeSyncOperationsRef.current.size > 0 ||
          syncStatusEpochRef.current !== pulseEpoch
        ) {
          return;
        }

        setSyncStatus(resolveIdleSyncStatus());
        syncIncomingTimeoutRef.current = null;
      }, 950);
    },
    [clearIncomingSyncTimeout, resolveIdleSyncStatus],
  );

  const { getCellEditFeedback, persistEditedRows } = useProcurementEditFeedback({
    companyId: company_id,
    projectId: id,
    accessLevel,
    alignedData,
    gridRef,
    beginSyncActivity,
    finishSyncActivity,
    failSyncActivity,
    setItemsPendingApprovals: setLiveItemsPendingApprovals,
  });

  const clearRemoteEditingPresence = useCallback(() => {
    remoteEditingCellByActorRef.current.clear();
    setEditingPresenceByCellKey({});
  }, []);

  const postCellPresenceRequest = useCallback(
    (serviceMethodName, cellLocation) => {
      const entryID = firstFilled(cellLocation?.entryID);
      const columnName = firstFilled(cellLocation?.columnName);

      if (!entryID || !columnName) {
        return;
      }

      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");

      ProjectServices?.[serviceMethodName]?.(
        { entryID, columnName },
        {
          company_id,
          project_id: id,
          code,
          loginEmail,
        },
      ).catch((error) => {
        console.error(
          `Failed to ${serviceMethodName} for procurement cell`,
          error,
        );
      });
    },
    [company_id, id],
  );

  const handleCellEditingStarted = useCallback(
    (params) => {
      if (params.data?.rowType !== "submittal") {
        return;
      }

      const entryID = firstFilled(params.data?.entryID);
      const columnName = firstFilled(params.colDef?.field);
      const cellKey = buildProcurementEditingCellKey(entryID, columnName);

      if (!cellKey) {
        return;
      }

      if (localEditingCellRef.current?.key === cellKey) {
        return;
      }

      if (localEditingCellRef.current) {
        postCellPresenceRequest("userOutOfCell", localEditingCellRef.current);
      }

      const nextEditingCell = { entryID, columnName, key: cellKey };
      localEditingCellRef.current = nextEditingCell;
      postCellPresenceRequest("userInCell", nextEditingCell);
    },
    [postCellPresenceRequest],
  );

  const handleCellEditingStopped = useCallback(
    (params) => {
      if (params.data?.rowType !== "submittal") {
        return;
      }

      const entryID = firstFilled(params.data?.entryID);
      const columnName = firstFilled(params.colDef?.field);
      const cellKey = buildProcurementEditingCellKey(entryID, columnName);

      if (!cellKey || localEditingCellRef.current?.key !== cellKey) {
        return;
      }

      const previousEditingCell = localEditingCellRef.current;
      localEditingCellRef.current = null;
      postCellPresenceRequest("userOutOfCell", previousEditingCell);
    },
    [postCellPresenceRequest],
  );

  const handleRetrySync = useCallback(() => {
    manualSyncRetryPendingRef.current = true;
    clearIncomingSyncTimeout();
    setSyncStatus({
      state: "syncing",
      message: "Refreshing latest data",
    });
    reloadData();
  }, [clearIncomingSyncTimeout, reloadData]);

  useEffect(() => {
    if (!manualSyncRetryPendingRef.current || isLoading) {
      return;
    }

    manualSyncRetryPendingRef.current = false;

    if (loadError) {
      setSyncStatus({
        state: "error",
        message:
          "Failed to refresh the latest data. You may no longer be synced to the latest version.",
      });
      return;
    }

    if (socketSyncErrorMessage) {
      setSyncStatus({
        state: "error",
        message: socketSyncErrorMessage,
      });
      return;
    }

    setSyncStatus(resolveIdleSyncStatus("Latest data refreshed"));
  }, [isLoading, loadError, resolveIdleSyncStatus, socketSyncErrorMessage]);

  useEffect(() => {
    setSelectedView(lastUsedView || undefined);
  }, [lastUsedView]);

  useEffect(() => {
    if (accessLevel !== null && accessLevel !== undefined) {
      dispatch(projectsAccessLevel(accessLevel));
    }
  }, [accessLevel, dispatch]);

  useEffect(() => {
    if (projectName) {
      dispatch(SetProjectName(projectName));
    }
  }, [dispatch, projectName]);

  const SelectViewOptions = useMemo(
    () =>
      (viewNames || []).map((item) => ({
        label: item,
        value: item,
      })),
    [viewNames],
  );
  const assignedFilterOptions = useMemo(
    () =>
      Object.entries(liveTasksDict || {})
        .map(([email, value]) => ({
          value: email,
          tagLabel: firstFilled(value?.fullName, email),
          label: firstFilled(value?.fullName)
            ? `${value.fullName} (${email})`
            : email,
        }))
        .sort((left, right) =>
          left.label.localeCompare(right.label, undefined, {
            sensitivity: "base",
            numeric: true,
          }),
        ),
    [liveTasksDict],
  );

  const formulaColumns = useMemo(
    () => buildFormulaColumns(alignedData),
    [alignedData],
  );
  const scheduleSyncContext = useMemo(
    () => buildScheduleSyncContext(alignedData, schedule),
    [alignedData, schedule],
  );
  const businessCalendarContext = useMemo(
    () => buildBusinessCalendarContext(workingDays, nonWorkingDays),
    [workingDays, nonWorkingDays],
  );
  const groupingSettings = useMemo(
    () => getGroupingSettings(settingsInfo),
    [settingsInfo],
  );
  const columnPermissions = useMemo(
    () => buildColumnPermissions(liveUsersInfo),
    [liveUsersInfo],
  );
  const editingUserDirectory = useMemo(
    () => buildProcurementEditingUserDirectory(liveTasksDict, liveUsersInfo),
    [liveTasksDict, liveUsersInfo],
  );

  const scheduleOptions = useMemo(
    () =>
      (schedule || [])
        .map((entry) => {
          const [value, activity] = Object.entries(entry || {})[0] || [];
          const normalizedValue = firstFilled(value);

          if (!normalizedValue) {
            return null;
          }

          const taskName = firstFilled(activity?.taskName);

          return {
            value: normalizedValue,
            label: taskName
              ? `${normalizedValue} - ${taskName}`
              : normalizedValue,
          };
        })
        .filter(Boolean)
        .sort((left, right) =>
          left.value.localeCompare(right.value, undefined, {
            numeric: true,
            sensitivity: "base",
          }),
        ),
    [schedule],
  );

  useEffect(() => {
    const nextRows = applyFormulaColumnsToRows(
      rowsData,
      formulaColumns,
      alignedData,
      scheduleSyncContext,
      settingsInfo,
      businessCalendarContext,
    );
    pendingInsertedDraftsRef.current = [];
    gridRowsDataRef.current = nextRows;
    appliedRowsDataRef.current = rowsData;
    setGridRowsData(nextRows);
  }, [
    rowsData,
    formulaColumns,
    alignedData,
    scheduleSyncContext,
    settingsInfo,
    businessCalendarContext,
  ]);

  useEffect(() => {
    gridRowsDataRef.current = gridRowsData;
  }, [gridRowsData]);

  // Keeps recently removed rows around so restore-hidden / restore-deleted socket events can reinsert them without a full reload.
  const rememberRemovedProcurementRows = useCallback((rowsToRemember) => {
    if (!rowsToRemember?.length) {
      return;
    }

    rowsToRemember.forEach((row) => {
      const rowEntryId = firstFilled(row?.entryID, row?.tempId, row?.id);
      if (!rowEntryId) {
        return;
      }

      removedProcurementRowsRef.current.set(String(rowEntryId), {
        ...row,
      });
    });

    while (removedProcurementRowsRef.current.size > 500) {
      const oldestKey = removedProcurementRowsRef.current.keys().next().value;
      if (!oldestKey) {
        break;
      }
      removedProcurementRowsRef.current.delete(oldestKey);
    }
  }, []);

  // Removes one or more procurement rows from local state while preserving unrelated selection and draft state.
  const removeProcurementRowsById = useCallback(
    (
      deletedEntryIds,
      { clearSelection = false, removePendingApprovals = false } = {},
    ) => {
      if (!deletedEntryIds?.length) {
        return;
      }

      const deletedEntryIdSet = new Set(
        deletedEntryIds.map((entryId) => String(entryId)),
      );

      if (removePendingApprovals) {
        setDeletedEntryIds((previousEntryIds) => [
          ...new Set([...previousEntryIds, ...deletedEntryIdSet]),
        ]);
        setLiveItemsPendingApprovals((previousEntryIds) =>
          previousEntryIds.filter(
            (entryId) => !deletedEntryIdSet.has(String(entryId)),
          ),
        );
      }

      pendingInsertedDraftsRef.current =
        pendingInsertedDraftsRef.current.filter(
          (pendingDraft) => !deletedEntryIdSet.has(String(pendingDraft.tempId)),
        );

      setGridRowsData((previousRows) => {
        const removedRows = [];
        const nextRows = previousRows.filter((row) => {
          const rowIdentity = firstFilled(row.entryID, row.tempId, row.id);
          const shouldRemove = deletedEntryIdSet.has(String(rowIdentity));

          if (shouldRemove) {
            removedRows.push(row);
          }

          return !shouldRemove;
        });

        rememberRemovedProcurementRows(removedRows);
        gridRowsDataRef.current = nextRows;
        return nextRows;
      });

      setSelectedIds((previousSelectedIds) => {
        if (clearSelection) {
          return new Set();
        }

        let hasChanged = false;
        const nextSelectedIds = new Set();

        previousSelectedIds.forEach((selectedId) => {
          const normalizedSelectedId = String(selectedId).replace(
            /^submittal:/,
            "",
          );

          if (deletedEntryIdSet.has(normalizedSelectedId)) {
            hasChanged = true;
            return;
          }

          nextSelectedIds.add(selectedId);
        });

        return hasChanged ? nextSelectedIds : previousSelectedIds;
      });
    },
    [rememberRemovedProcurementRows],
  );

  // Applies a critical-path flagged/unflagged state to one or more rows without disturbing unrelated grid state.
  const setProcurementCriticalPathState = useCallback((entryIds, isFlagged) => {
    if (!entryIds?.length) {
      return;
    }

    const normalizedEntryIds = new Set(
      entryIds.map((entryId) => String(entryId)),
    );
    const nextCriticalPathValue = isFlagged ? "True" : "False";

    setGridRowsData((previousRows) => {
      let hasChanged = false;
      const nextRows = previousRows.map((row) => {
        const rowEntryId = firstFilled(row?.entryID, row?.tempId, row?.id);

        if (!normalizedEntryIds.has(String(rowEntryId))) {
          return row;
        }

        if (row?.criticalPathFlag === nextCriticalPathValue) {
          return row;
        }

        hasChanged = true;
        return {
          ...row,
          criticalPathFlag: nextCriticalPathValue,
        };
      });

      if (!hasChanged) {
        return previousRows;
      }

      gridRowsDataRef.current = nextRows;
      return nextRows;
    });
  }, []);

  // Reinserts one or more restored procurement rows into local state and recalculates any derived values.
  const restoreProcurementRowsLocally = useCallback(
    (rowsToRestore) => {
      if (!rowsToRestore?.length) {
        return;
      }

      setGridRowsData((previousRows) => {
        const existingRowIndexes = new Map(
          previousRows.map((row, index) => [
            String(firstFilled(row?.entryID, row?.tempId, row?.id)),
            index,
          ]),
        );
        const nextRows = [...previousRows];
        let hasChanged = false;

        rowsToRestore.forEach((restoredRow) => {
          const restoredEntryId = firstFilled(
            restoredRow?.entryID,
            restoredRow?.tempId,
            restoredRow?.id,
          );

          if (!restoredEntryId) {
            return;
          }

          removedProcurementRowsRef.current.delete(String(restoredEntryId));

          const existingIndex = existingRowIndexes.get(String(restoredEntryId));

          if (existingIndex === undefined) {
            nextRows.push(restoredRow);
            existingRowIndexes.set(
              String(restoredEntryId),
              nextRows.length - 1,
            );
            hasChanged = true;
            return;
          }

          const mergedRow = {
            ...nextRows[existingIndex],
            ...restoredRow,
          };
          const didRowChange = Object.keys(restoredRow).some(
            (key) => nextRows[existingIndex]?.[key] !== mergedRow[key],
          );

          if (!didRowChange) {
            return;
          }

          nextRows[existingIndex] = mergedRow;
          hasChanged = true;
        });

        if (!hasChanged) {
          return previousRows;
        }

        const recalculatedRows = applyFormulaColumnsToRows(
          nextRows,
          formulaColumns,
          alignedData,
          scheduleSyncContext,
          settingsInfo,
          businessCalendarContext,
        );
        gridRowsDataRef.current = recalculatedRows;
        return recalculatedRows;
      });
    },
    [
      alignedData,
      businessCalendarContext,
      formulaColumns,
      scheduleSyncContext,
      settingsInfo,
    ],
  );

  // Flushes any queued AG Grid cell flashes, retrying briefly if the updated row nodes are still mounting.
  const flushPendingProcurementCellFlash = useCallback(
    (attemptsRemaining = 4) => {
      const api = gridRef.current?.api;
      const entriesToFlash = pendingCellFlashRef.current;

      if (!entriesToFlash.length) {
        flashFlushScheduledRef.current = false;
        return;
      }

      if (!api) {
        if (attemptsRemaining > 0 && typeof window !== "undefined") {
          window.setTimeout(() => {
            flushPendingProcurementCellFlash(attemptsRemaining - 1);
          }, 60);
          return;
        }

        pendingCellFlashRef.current = [];
        flashFlushScheduledRef.current = false;
        return;
      }

      const retryEntries = [];

      entriesToFlash.forEach(({ entryId, columnIds }) => {
        let rowNode = api.getRowNode(`submittal:${entryId}`);
        if (!rowNode) {
          api.forEachNode((node) => {
            if (!rowNode && node?.data?.id === `submittal:${entryId}`) {
              rowNode = node;
            }
          });
        }
        const flashableColumns = (columnIds || []).filter(
          (columnId) => Boolean(columnId) && columnId !== "entryID",
        );

        if (!rowNode || !flashableColumns.length) {
          retryEntries.push({ entryId, columnIds: flashableColumns });
          return;
        }

        api.flashCells({
          rowNodes: [rowNode],
          columns: flashableColumns,
          flashDelay: 1400,
          fadeDelay: 1100,
        });
      });

      pendingCellFlashRef.current = retryEntries;

      if (
        retryEntries.length &&
        attemptsRemaining > 0 &&
        typeof window !== "undefined"
      ) {
        window.setTimeout(() => {
          flushPendingProcurementCellFlash(attemptsRemaining - 1);
        }, 60);
        return;
      }

      pendingCellFlashRef.current = [];
      flashFlushScheduledRef.current = false;
    },
    [],
  );

  // Queues AG Grid cell flashes and schedules a direct flush after the current update cycle.
  const queueProcurementCellFlash = useCallback(
    (flashEntries) => {
      if (
        !Array.isArray(flashEntries) ||
        !flashEntries.length ||
        typeof window === "undefined"
      ) {
        return;
      }

      pendingCellFlashRef.current = [
        ...pendingCellFlashRef.current,
        ...flashEntries,
      ];

      if (flashFlushScheduledRef.current) {
        return;
      }

      flashFlushScheduledRef.current = true;
      window.setTimeout(() => {
        flushPendingProcurementCellFlash();
      }, 0);
    },
    [flushPendingProcurementCellFlash],
  );

  // Applies any queued remote edit socket events in one pass so burst updates do not make the whole table blink.
  const flushPendingSocketEditedRows = useCallback(() => {
    if (
      pendingSocketEditFlushTimeoutRef.current !== null &&
      typeof window !== "undefined"
    ) {
      window.clearTimeout(pendingSocketEditFlushTimeoutRef.current);
    }
    pendingSocketEditFlushTimeoutRef.current = null;

    const pendingEditedSocketRows = [
      ...pendingSocketEditRowsRef.current.values(),
    ];
    pendingSocketEditRowsRef.current.clear();

    if (!pendingEditedSocketRows.length) {
      return;
    }

    setGridRowsData((previousRows) => {
      const previousRowsById = new Map(
        previousRows.map((row, index) => [
          getProcurementRowId(row, index),
          row,
        ]),
      );
      const rowPatches = new Map();

      pendingEditedSocketRows.forEach((editedSocketRow) => {
        const editedEntryId = getProcurementSocketEntryId(editedSocketRow);
        if (!editedEntryId) {
          return;
        }

        const existingRow = previousRowsById.get(editedEntryId) || null;

        if (!existingRow) {
          return;
        }

        const rowPatch = buildProcurementSocketRowPatch(
          existingRow,
          editedSocketRow,
          alignedData,
        );

        if (!Object.keys(rowPatch).length) {
          return;
        }

        rowPatches.set(editedEntryId, rowPatch);
      });

      if (!rowPatches.size) {
        return previousRows;
      }

      pulseIncomingSyncActivity("Receiving live updates");

      const { recalculatedRows, changedRowUpdates } = applyLocalRowPatches({
        previousRows,
        rowPatches,
        formulaColumns,
        alignedData,
        scheduleSyncContext,
        settingsInfo,
        overwriteCalculations: groupingSettings.overwriteCalculations,
        businessCalendarContext,
        markEdited: false,
      });

      const flashEntries = changedRowUpdates
        .map((changedRowUpdate) => ({
          entryId: String(changedRowUpdate.entryID),
          columnIds: Object.keys(changedRowUpdate).filter(
            (key) => key !== "entryID",
          ),
        }))
        .filter(({ columnIds }) => columnIds.length > 0);

      if (flashEntries.length) {
        queueProcurementCellFlash(flashEntries);
      }

      gridRowsDataRef.current = recalculatedRows;
      return recalculatedRows;
    });
  }, [
    alignedData,
    businessCalendarContext,
    formulaColumns,
    groupingSettings.overwriteCalculations,
    pulseIncomingSyncActivity,
    queueProcurementCellFlash,
    scheduleSyncContext,
    settingsInfo,
  ]);

  const handleSocketUserInCell = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);
      const actorEmail = getProcurementSocketActorEmail(socketMessage);
      const currentUserEmail = normalizeText(
        AuthStorage.getItem("email"),
      ).toLowerCase();

      if (actorEmail && actorEmail === currentUserEmail) {
        return;
      }

      if (
        !doesProcurementSocketMatchCurrentProject(
          socketMessage,
          null,
          company_id,
          id,
        )
      ) {
        return;
      }

      const cellLocation = getProcurementSocketCellLocation(socketMessage);
      const cellKey = buildProcurementEditingCellKey(
        cellLocation?.entryID,
        cellLocation?.columnName,
      );

      if (!actorEmail || !cellKey) {
        return;
      }

      const theme = getProcurementEditingPresenceTheme(actorEmail);
      const label = formatProcurementEditingUserLabel(
        actorEmail,
        editingUserDirectory,
        socketMessage,
      );

      setEditingPresenceByCellKey((previousState) => {
        const nextState = { ...previousState };
        const previousCellKey =
          remoteEditingCellByActorRef.current.get(actorEmail);

        if (previousCellKey && nextState[previousCellKey]) {
          const previousCellPresences = nextState[previousCellKey].filter(
            (presence) => presence.email !== actorEmail,
          );

          if (previousCellPresences.length > 0) {
            nextState[previousCellKey] = previousCellPresences;
          } else {
            delete nextState[previousCellKey];
          }
        }

        nextState[cellKey] = [
          ...(nextState[cellKey] || []).filter(
            (presence) => presence.email !== actorEmail,
          ),
          {
            email: actorEmail,
            label,
            color: theme.color,
            backgroundColor: theme.backgroundColor,
          },
        ].sort((left, right) =>
          left.label.localeCompare(right.label, undefined, {
            sensitivity: "base",
          }),
        );

        remoteEditingCellByActorRef.current.set(actorEmail, cellKey);
        return nextState;
      });
    },
    [company_id, editingUserDirectory, id],
  );

  const handleSocketUserOutOfCell = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);
      const actorEmail = getProcurementSocketActorEmail(socketMessage);
      const currentUserEmail = normalizeText(
        AuthStorage.getItem("email"),
      ).toLowerCase();

      if (actorEmail && actorEmail === currentUserEmail) {
        return;
      }

      if (
        !doesProcurementSocketMatchCurrentProject(
          socketMessage,
          null,
          company_id,
          id,
        )
      ) {
        return;
      }

      const cellLocation = getProcurementSocketCellLocation(socketMessage);
      const explicitCellKey = buildProcurementEditingCellKey(
        cellLocation?.entryID,
        cellLocation?.columnName,
      );
      const previousCellKey =
        remoteEditingCellByActorRef.current.get(actorEmail);
      const cellKeysToClear = explicitCellKey
        ? [explicitCellKey]
        : previousCellKey
          ? [previousCellKey]
          : [];

      if (!actorEmail || !cellKeysToClear.length) {
        return;
      }

      setEditingPresenceByCellKey((previousState) => {
        const nextState = { ...previousState };

        cellKeysToClear.forEach((cellKey) => {
          if (!nextState[cellKey]) {
            return;
          }

          const remainingPresences = nextState[cellKey].filter(
            (presence) => presence.email !== actorEmail,
          );

          if (remainingPresences.length > 0) {
            nextState[cellKey] = remainingPresences;
          } else {
            delete nextState[cellKey];
          }
        });

        return nextState;
      });

      if (!explicitCellKey || previousCellKey === explicitCellKey) {
        remoteEditingCellByActorRef.current.delete(actorEmail);
      }
    },
    [company_id, id],
  );

  // Handles inserted-row socket events, logging the payload and syncing temp drafts or appending remote inserts.
  const handleSocketInsertedRow = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      console.log("New entry edited by:", socketMessage?.actor);
      console.log("Data:", socketMessage?.data);

      const currentUserEmail = normalizeText(
        AuthStorage.getItem("email"),
      ).toLowerCase();
      const messageActorEmail = getProcurementSocketActorEmail(socketMessage);

      if (currentUserEmail && currentUserEmail === messageActorEmail) {
        return;
      }

      const incomingSocketRows = Array.isArray(socketMessage?.data)
        ? socketMessage.data.filter(
            (row) => row && typeof row === "object" && !Array.isArray(row),
          )
        : socketMessage?.data && typeof socketMessage.data === "object"
          ? [socketMessage.data]
          : [];

      if (!incomingSocketRows.length) {
        return;
      }

      pulseIncomingSyncActivity("Receiving live updates");

      incomingSocketRows.forEach((incomingSocketRow) => {
        if (
          !doesProcurementSocketMatchCurrentProject(
            socketMessage,
            incomingSocketRow,
            company_id,
            id,
          )
        ) {
          return;
        }

        const incomingEntryId = String(
          firstFilled(
            incomingSocketRow.entryID,
            incomingSocketRow.newEntryID,
            incomingSocketRow.id,
          ),
        );

        if (!incomingEntryId) {
          return;
        }

        let matchedTempRowId = "";

        setGridRowsData((previousRows) => {
          const incomingBaseRow = buildProcurementSocketBaseRow(
            incomingSocketRow,
            incomingEntryId,
          );

          if (!incomingBaseRow) {
            return previousRows;
          }

          const existingPersistedRowIndex = previousRows.findIndex(
            (row) => String(firstFilled(row?.entryID)) === incomingEntryId,
          );

          if (existingPersistedRowIndex >= 0) {
            const nextRows = [...previousRows];
            nextRows[existingPersistedRowIndex] = {
              ...nextRows[existingPersistedRowIndex],
              ...incomingBaseRow,
            };
            const recalculatedRows = applyFormulaColumnsToRows(
              nextRows,
              formulaColumns,
              alignedData,
              scheduleSyncContext,
              settingsInfo,
              businessCalendarContext,
            );
            gridRowsDataRef.current = recalculatedRows;
            return recalculatedRows;
          }

          const incomingTempId = firstFilled(
            incomingSocketRow.tempId,
            incomingSocketRow.clientTempId,
          );
          const incomingSignature =
            buildPendingInsertSignature(incomingSocketRow);
          const pendingDrafts = pendingInsertedDraftsRef.current;
          let matchedPendingIndex = incomingTempId
            ? pendingDrafts.findIndex(
                (pendingDraft) => pendingDraft.tempId === incomingTempId,
              )
            : -1;

          if (matchedPendingIndex < 0 && incomingSignature) {
            matchedPendingIndex = pendingDrafts.findIndex(
              (pendingDraft) => pendingDraft.signature === incomingSignature,
            );
          }

          if (matchedPendingIndex < 0 && pendingDrafts.length === 1) {
            matchedPendingIndex = 0;
          }

          if (matchedPendingIndex < 0) {
            const nextRows = [...previousRows, incomingBaseRow];
            const recalculatedRows = applyFormulaColumnsToRows(
              nextRows,
              formulaColumns,
              alignedData,
              scheduleSyncContext,
              settingsInfo,
              businessCalendarContext,
            );
            gridRowsDataRef.current = recalculatedRows;
            return recalculatedRows;
          }

          const matchedPendingDraft = pendingDrafts[matchedPendingIndex];
          const localDraftIndex = previousRows.findIndex(
            (row, index) =>
              getProcurementRowId(row, index) === matchedPendingDraft.tempId,
          );

          pendingInsertedDraftsRef.current = pendingDrafts.filter(
            (_, index) => index !== matchedPendingIndex,
          );

          if (localDraftIndex < 0) {
            const nextRows = [...previousRows, incomingBaseRow];
            const recalculatedRows = applyFormulaColumnsToRows(
              nextRows,
              formulaColumns,
              alignedData,
              scheduleSyncContext,
              settingsInfo,
              businessCalendarContext,
            );
            gridRowsDataRef.current = recalculatedRows;
            return recalculatedRows;
          }

          matchedTempRowId = matchedPendingDraft.tempId;
          const nextRows = [...previousRows];
          nextRows[localDraftIndex] = mergeSocketInsertedRow(
            previousRows[localDraftIndex],
            incomingBaseRow,
            incomingEntryId,
          );
          const recalculatedRows = applyFormulaColumnsToRows(
            nextRows,
            formulaColumns,
            alignedData,
            scheduleSyncContext,
            settingsInfo,
            businessCalendarContext,
          );
          gridRowsDataRef.current = recalculatedRows;
          return recalculatedRows;
        });

        if (matchedTempRowId) {
          setSelectedIds((previousSelectedIds) => {
            const tempSelectedId = `submittal:${matchedTempRowId}`;

            if (!previousSelectedIds.has(tempSelectedId)) {
              return previousSelectedIds;
            }

            const nextSelectedIds = new Set(previousSelectedIds);
            nextSelectedIds.delete(tempSelectedId);
            nextSelectedIds.add(`submittal:${incomingEntryId}`);
            return nextSelectedIds;
          });

          setAssignDrawerRowId((previousRowId) =>
            previousRowId === matchedTempRowId
              ? incomingEntryId
              : previousRowId,
          );
        }
      });
    },
    [
      alignedData,
      businessCalendarContext,
      company_id,
      formulaColumns,
      id,
      pulseIncomingSyncActivity,
      scheduleSyncContext,
      settingsInfo,
    ],
  );

  // Handles deleted-row socket events so remote removals disappear live without double-applying the local user's own delete.
  const handleSocketDeletedRow = useCallback(
    (message, isDeletion = true) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      console.log("Deleted entry by:", socketMessage?.actor);
      console.log("Deleted data:", socketMessage?.data);

      const currentUserEmail = normalizeText(
        AuthStorage.getItem("email"),
      ).toLowerCase();
      const messageActorEmail = getProcurementSocketActorEmail(socketMessage);

      if (currentUserEmail && currentUserEmail === messageActorEmail) {
        return;
      }

      if (
        !doesProcurementSocketMatchCurrentProject(
          socketMessage,
          null,
          company_id,
          id,
        )
      ) {
        return;
      }

      const deletedEntryIds =
        getProcurementSocketDeletedEntryIds(socketMessage);
      if (!deletedEntryIds.length) {
        return;
      }

      pulseIncomingSyncActivity("Receiving live updates");
      removeProcurementRowsById(deletedEntryIds, {
        removePendingApprovals: isDeletion,
      });
    },
    [company_id, id, pulseIncomingSyncActivity, removeProcurementRowsById],
  );

  // Handles live row edits from other users and flashes only the cells whose values actually changed.
  const handleSocketEditedRow = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      console.log("Edited entry by:", socketMessage?.actor);
      console.log("Edited data:", socketMessage?.data);

      const currentUserEmail = normalizeText(
        AuthStorage.getItem("email"),
      ).toLowerCase();
      const messageActorEmail = getProcurementSocketActorEmail(socketMessage);
      const isSelfSocketMessage =
        currentUserEmail && currentUserEmail === messageActorEmail;

      const editedSocketRows = Array.isArray(socketMessage?.data)
        ? socketMessage.data.filter(
            (row) => row && typeof row === "object" && !Array.isArray(row),
          )
        : socketMessage?.data && typeof socketMessage.data === "object"
          ? [socketMessage.data]
          : [];

      if (!editedSocketRows.length) {
        return;
      }

      if (isSelfSocketMessage) {
        setGridRowsData((previousRows) => {
          const previousRowsById = new Map(
            previousRows.map((row, index) => [
              getProcurementRowId(row, index),
              row,
            ]),
          );
          const rowPatches = new Map();

          editedSocketRows.forEach((editedSocketRow) => {
            if (
              !doesProcurementSocketMatchCurrentProject(
                socketMessage,
                editedSocketRow,
                company_id,
                id,
              ) ||
              !hasProcurementSocketParentReference(editedSocketRow)
            ) {
              return;
            }

            const editedEntryId = getProcurementSocketEntryId(editedSocketRow);
            if (!editedEntryId) {
              return;
            }

            const existingRow = previousRowsById.get(editedEntryId) || null;
            if (!existingRow) {
              return;
            }

            const rowPatch = buildProcurementSocketRowPatch(
              existingRow,
              editedSocketRow,
              alignedData,
            );
            const linkagePatch = {};

            if (Object.prototype.hasOwnProperty.call(rowPatch, "parentID")) {
              linkagePatch.parentID = rowPatch.parentID;
            }

            if (Object.prototype.hasOwnProperty.call(rowPatch, "parentId")) {
              linkagePatch.parentId = rowPatch.parentId;
            }

            if (!Object.keys(linkagePatch).length) {
              return;
            }

            rowPatches.set(editedEntryId, linkagePatch);
          });

          if (!rowPatches.size) {
            return previousRows;
          }

          const { recalculatedRows } = applyLocalRowPatches({
            previousRows,
            rowPatches,
            formulaColumns,
            alignedData,
            scheduleSyncContext,
            settingsInfo,
            overwriteCalculations: groupingSettings.overwriteCalculations,
            businessCalendarContext,
            markEdited: false,
          });

          gridRowsDataRef.current = recalculatedRows;
          return recalculatedRows;
        });

        return;
      }

      let hasQueuedEditedRow = false;

      editedSocketRows.forEach((editedSocketRow) => {
        if (
          !doesProcurementSocketMatchCurrentProject(
            socketMessage,
            editedSocketRow,
            company_id,
            id,
          )
        ) {
          return;
        }

        const editedEntryId = getProcurementSocketEntryId(editedSocketRow);
        if (!editedEntryId) {
          return;
        }

        const queuedSocketRow =
          pendingSocketEditRowsRef.current.get(editedEntryId) || {};

        pendingSocketEditRowsRef.current.set(editedEntryId, {
          ...queuedSocketRow,
          ...editedSocketRow,
          entryID: editedEntryId,
        });
        hasQueuedEditedRow = true;
      });

      if (!hasQueuedEditedRow) {
        return;
      }

      if (
        pendingSocketEditFlushTimeoutRef.current !== null ||
        typeof window === "undefined"
      ) {
        return;
      }

      pendingSocketEditFlushTimeoutRef.current = window.setTimeout(() => {
        flushPendingSocketEditedRows();
      }, SOCKET_EDIT_BATCH_WINDOW_MS);
    },
    [
      alignedData,
      businessCalendarContext,
      company_id,
      formulaColumns,
      flushPendingSocketEditedRows,
      groupingSettings.overwriteCalculations,
      id,
      scheduleSyncContext,
      settingsInfo,
    ],
  );

  // Applies Procore-assigned identifiers to every open view, including the user who posted the submittal.
  const handleSocketPostToProcore = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);
      const postToProcoreRows = Array.isArray(socketMessage?.data)
        ? socketMessage.data.filter(
            (row) => row && typeof row === "object" && !Array.isArray(row),
          )
        : socketMessage?.data && typeof socketMessage.data === "object"
          ? [socketMessage.data]
          : socketMessage && typeof socketMessage === "object"
            ? [socketMessage]
            : [];

      const matchingRows = postToProcoreRows.filter((socketRow) =>
        doesProcurementSocketMatchCurrentProject(
          socketMessage,
          socketRow,
          company_id,
          id,
        ),
      );

      if (!matchingRows.length) {
        return;
      }

      setGridRowsData((previousRows) => {
        const previousRowsById = new Map(
          previousRows.map((row, index) => [
            String(getProcurementRowId(row, index)),
            row,
          ]),
        );
        const rowPatches = new Map();

        matchingRows.forEach((socketRow) => {
          const entryId = getProcurementSocketEntryId(socketRow);
          const existingRow = previousRowsById.get(String(entryId));

          if (!entryId || !existingRow) {
            return;
          }

          const rowPatch = buildProcurementSocketRowPatch(
            existingRow,
            socketRow,
            alignedData,
          );

          if (Object.keys(rowPatch).length) {
            rowPatches.set(String(entryId), rowPatch);
          }
        });

        if (!rowPatches.size) {
          return previousRows;
        }

        pulseIncomingSyncActivity("Receiving Procore updates");
        const { recalculatedRows, changedRowUpdates } = applyLocalRowPatches({
          previousRows,
          rowPatches,
          formulaColumns,
          alignedData,
          scheduleSyncContext,
          settingsInfo,
          overwriteCalculations: groupingSettings.overwriteCalculations,
          businessCalendarContext,
          markEdited: false,
        });

        const flashEntries = changedRowUpdates
          .map((changedRowUpdate) => ({
            entryId: String(changedRowUpdate.entryID),
            columnIds: Object.keys(changedRowUpdate).filter(
              (key) => key !== "entryID",
            ),
          }))
          .filter(({ columnIds }) => columnIds.length > 0);

        if (flashEntries.length) {
          queueProcurementCellFlash(flashEntries);
        }

        gridRowsDataRef.current = recalculatedRows;
        return recalculatedRows;
      });
    },
    [
      alignedData,
      businessCalendarContext,
      company_id,
      formulaColumns,
      groupingSettings.overwriteCalculations,
      id,
      pulseIncomingSyncActivity,
      queueProcurementCellFlash,
      scheduleSyncContext,
      settingsInfo,
    ],
  );

  // Handles restore-deleted and restore-hidden socket events so restored rows reappear live.
  const handleSocketRestoredDeletedRow = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      console.log("Restored deleted entry by:", socketMessage?.actor);
      console.log("Restored deleted data:", socketMessage?.data);

      const restoredSocketRows = Array.isArray(socketMessage?.data)
        ? socketMessage.data.filter(
            (row) => row && typeof row === "object" && !Array.isArray(row),
          )
        : socketMessage?.data && typeof socketMessage.data === "object"
          ? [socketMessage.data]
          : [];
      const restoredEntryIds =
        getProcurementSocketDeletedEntryIds(socketMessage);

      const normalizedRestoredRows = restoredSocketRows
        .filter((restoredSocketRow) =>
          doesProcurementSocketMatchCurrentProject(
            socketMessage,
            restoredSocketRow,
            company_id,
            id,
          ),
        )
        .map((restoredSocketRow) =>
          buildProcurementSocketBaseRow(restoredSocketRow),
        )
        .filter(Boolean);

      let rowsToRestore = normalizedRestoredRows;

      if (!rowsToRestore.length && restoredEntryIds.length) {
        if (
          !doesProcurementSocketMatchCurrentProject(
            socketMessage,
            null,
            company_id,
            id,
          )
        ) {
          return;
        }

        rowsToRestore = restoredEntryIds
          .map((entryId) =>
            buildProcurementSocketBaseRow(
              removedProcurementRowsRef.current.get(String(entryId)),
              entryId,
            ),
          )
          .filter(Boolean);
      }

      if (!rowsToRestore.length) {
        return;
      }

      pulseIncomingSyncActivity("Receiving live updates");
      restoreProcurementRowsLocally(rowsToRestore);
    },
    [company_id, id, pulseIncomingSyncActivity, restoreProcurementRowsLocally],
  );

  // Handles critical-path flag and unflag socket events so other viewers stay in sync.
  const handleSocketCriticalPathRow = useCallback(
    (message, isFlagged) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      console.log(
        isFlagged
          ? "Critical path entry flagged by:"
          : "Critical path entry unflagged by:",
        socketMessage?.actor,
      );
      console.log(
        isFlagged
          ? "Critical path flagged data:"
          : "Critical path unflagged data:",
        socketMessage?.data,
      );

      const socketRows = Array.isArray(socketMessage?.data)
        ? socketMessage.data.filter(
            (row) => row && typeof row === "object" && !Array.isArray(row),
          )
        : socketMessage?.data && typeof socketMessage.data === "object"
          ? [socketMessage.data]
          : [];

      const entryIdsFromRows = socketRows
        .filter((socketRow) =>
          doesProcurementSocketMatchCurrentProject(
            socketMessage,
            socketRow,
            company_id,
            id,
          ),
        )
        .map((socketRow) => getProcurementSocketEntryId(socketRow))
        .filter(Boolean);

      let targetEntryIds = entryIdsFromRows;

      if (!targetEntryIds.length) {
        if (
          !doesProcurementSocketMatchCurrentProject(
            socketMessage,
            null,
            company_id,
            id,
          )
        ) {
          return;
        }

        targetEntryIds = getProcurementSocketDeletedEntryIds(socketMessage);
      }

      if (!targetEntryIds.length) {
        return;
      }

      pulseIncomingSyncActivity("Receiving live updates");
      setProcurementCriticalPathState(targetEntryIds, isFlagged);
    },
    [
      company_id,
      id,
      pulseIncomingSyncActivity,
      setProcurementCriticalPathState,
    ],
  );

  // Applies task assignment socket payloads to the live access-limited user state so assigned rows show/hide immediately.
  const handleSocketTaskAssignment = useCallback(
    (message, isAssigned) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      console.log(
        isAssigned ? "Task assigned data:" : "Task deleted data:",
        socketMessage,
      );

      if (
        !doesProcurementSocketMatchCurrentProject(
          socketMessage,
          null,
          company_id,
          id,
        )
      ) {
        return;
      }

      const socketRows = getProcurementSocketTaskRows(message, socketMessage);
      const normalizedSocketRows = socketRows.filter((socketRow) =>
        doesProcurementSocketMatchCurrentProject(
          socketMessage,
          socketRow,
          company_id,
          id,
        ),
      );
      const taskEntryIds = normalizedSocketRows.length
        ? normalizedSocketRows
            .flatMap((socketRow) => getProcurementSocketTaskEntryIds(socketRow))
            .filter(Boolean)
        : getProcurementSocketTaskEntryIds(socketMessage);
      const hasReplacementAssignments = Array.isArray(
        socketMessage?.taskIDsAssigned,
      );
      const currentUserEmail = normalizeText(
        AuthStorage.getItem("email"),
      ).toLowerCase();
      const assignedEmails = getProcurementSocketAssignedEmails(socketMessage);
      const currentUserInfo = Array.isArray(liveUsersInfo)
        ? liveUsersInfo[0] || {}
        : liveUsersInfo || {};
      const currentUserPermissions = buildColumnPermissions(currentUserInfo);
      const currentAssignedIds =
        getProcurementSocketTaskEntryIds(currentUserInfo);
      const isCurrentUserTargetedByTaskAssignment = assignedEmails.length
        ? assignedEmails.includes(currentUserEmail)
        : true;
      const showsOnlyAssignedLineItems =
        String(currentUserInfo?.viewOnlyAssignedEntriesGranularPermission) ===
        "3";
      const shouldUpdateCurrentUserAssignmentState =
        currentUserPermissions.accessLevel === 1 &&
        isCurrentUserTargetedByTaskAssignment;
      let nextAssignedIdsForCurrentUser = currentAssignedIds;

      pulseIncomingSyncActivity("Receiving live updates");

      if (shouldUpdateCurrentUserAssignmentState) {
        if (hasReplacementAssignments) {
          nextAssignedIdsForCurrentUser = taskEntryIds.map(String);
        } else if (taskEntryIds.length) {
          const nextAssignedIdSet = new Set(currentAssignedIds.map(String));

          taskEntryIds.forEach((entryId) => {
            if (isAssigned) {
              nextAssignedIdSet.add(String(entryId));
              return;
            }

            nextAssignedIdSet.delete(String(entryId));
          });

          nextAssignedIdsForCurrentUser = [...nextAssignedIdSet];
        }
      }

      const nextAssignedIdSetForCurrentUser = new Set(
        nextAssignedIdsForCurrentUser.map(String),
      );
      const previousAssignedIdSetForCurrentUser = new Set(
        currentAssignedIds.map(String),
      );
      const newlyAssignedEntryIdsForCurrentUser = [
        ...nextAssignedIdSetForCurrentUser,
      ].filter(
        (entryId) => !previousAssignedIdSetForCurrentUser.has(String(entryId)),
      );
      const newlyUnassignedEntryIdsForCurrentUser = [
        ...previousAssignedIdSetForCurrentUser,
      ].filter(
        (entryId) => !nextAssignedIdSetForCurrentUser.has(String(entryId)),
      );

      setLiveUsersInfo((previousUsersInfo) => {
        const currentUserInfo = Array.isArray(previousUsersInfo)
          ? previousUsersInfo[0] || {}
          : previousUsersInfo || {};

        const previousAssignedIds =
          getProcurementSocketTaskEntryIds(currentUserInfo);
        let nextAssignedIds = previousAssignedIds;

        if (
          shouldUpdateCurrentUserAssignmentState &&
          hasReplacementAssignments
        ) {
          nextAssignedIds = taskEntryIds.map(String);
        } else if (
          shouldUpdateCurrentUserAssignmentState &&
          taskEntryIds.length
        ) {
          const nextAssignedIdSet = new Set(previousAssignedIds.map(String));

          taskEntryIds.forEach((entryId) => {
            if (isAssigned) {
              nextAssignedIdSet.add(String(entryId));
              return;
            }

            nextAssignedIdSet.delete(String(entryId));
          });

          nextAssignedIds = [...nextAssignedIdSet];
        }

        const nextUserInfo = { ...currentUserInfo };

        TASK_ASSIGNMENT_USER_INFO_KEYS.forEach((key) => {
          if (
            socketMessage?.[key] !== null &&
            socketMessage?.[key] !== undefined
          ) {
            nextUserInfo[key] = socketMessage[key];
          }
        });

        nextUserInfo.taskIDsAssigned = nextAssignedIds.map((entryId) => [
          String(entryId),
        ]);

        return Array.isArray(previousUsersInfo) && previousUsersInfo.length > 0
          ? [nextUserInfo, ...previousUsersInfo.slice(1)]
          : [nextUserInfo];
      });

      setLiveTasksDict((previousTasksDict) => {
        const targetAssignmentEmails = assignedEmails.length
          ? assignedEmails
          : shouldUpdateCurrentUserAssignmentState
            ? [currentUserEmail]
            : [];

        if (!targetAssignmentEmails.length) {
          return previousTasksDict;
        }

        let hasChanged = false;
        const nextTasksDict = { ...(previousTasksDict || {}) };

        targetAssignmentEmails.forEach((targetEmail) => {
          const resolvedEmailKey =
            Object.keys(nextTasksDict).find(
              (email) => email.toLowerCase() === targetEmail.toLowerCase(),
            ) || targetEmail;
          const existingTaskInfo = nextTasksDict?.[resolvedEmailKey] || {};
          const previousAssignedIds = (existingTaskInfo?.entryIDs || []).map(
            String,
          );
          let nextAssignedIds = previousAssignedIds;

          if (hasReplacementAssignments) {
            nextAssignedIds = taskEntryIds.map(String);
          } else if (taskEntryIds.length) {
            const nextAssignedIdSet = new Set(previousAssignedIds);

            taskEntryIds.forEach((entryId) => {
              if (isAssigned) {
                nextAssignedIdSet.add(String(entryId));
                return;
              }

              nextAssignedIdSet.delete(String(entryId));
            });

            nextAssignedIds = [...nextAssignedIdSet];
          }

          const didEntryIdsChange =
            previousAssignedIds.length !== nextAssignedIds.length ||
            previousAssignedIds.some(
              (entryId, index) => entryId !== nextAssignedIds[index],
            );

          if (!didEntryIdsChange) {
            return;
          }

          hasChanged = true;
          nextTasksDict[resolvedEmailKey] = {
            ...existingTaskInfo,
            entryIDs: nextAssignedIds,
          };
        });

        return hasChanged ? nextTasksDict : previousTasksDict;
      });

      const shouldSyncAssignedRowVisibility =
        shouldUpdateCurrentUserAssignmentState &&
        currentUserPermissions.unlockedColumns instanceof Set &&
        currentUserPermissions.unlockedColumns.size > 0;

      if (
        shouldSyncAssignedRowVisibility &&
        showsOnlyAssignedLineItems &&
        newlyUnassignedEntryIdsForCurrentUser.length
      ) {
        removeProcurementRowsById(newlyUnassignedEntryIdsForCurrentUser);
      }

      if (
        shouldSyncAssignedRowVisibility &&
        newlyAssignedEntryIdsForCurrentUser.length
      ) {
        const existingVisibleEntryIds = new Set(
          (gridRowsDataRef.current || []).map((row) =>
            String(firstFilled(row?.entryID, row?.tempId, row?.id)),
          ),
        );
        const socketRowsByEntryId = new Map(
          normalizedSocketRows
            .map((socketRow) => {
              const [entryId = ""] =
                getProcurementSocketTaskEntryIds(socketRow);

              if (!entryId) {
                return null;
              }

              return [String(entryId), socketRow];
            })
            .filter(Boolean),
        );

        const rowsToRestore = newlyAssignedEntryIdsForCurrentUser
          .filter((entryId) => !existingVisibleEntryIds.has(String(entryId)))
          .map((entryId) => {
            const socketRow = socketRowsByEntryId.get(String(entryId));
            const cachedRow = removedProcurementRowsRef.current.get(
              String(entryId),
            );
            const loadedRow =
              (rowsData || []).find(
                (row) =>
                  String(firstFilled(row?.entryID, row?.tempId, row?.id)) ===
                  String(entryId),
              ) || null;

            return buildProcurementSocketBaseRow(
              socketRow || cachedRow || loadedRow,
              entryId,
            );
          })
          .filter(Boolean);

        if (rowsToRestore.length) {
          restoreProcurementRowsLocally(rowsToRestore);
        }
      }
    },
    [
      company_id,
      id,
      liveUsersInfo,
      pulseIncomingSyncActivity,
      removeProcurementRowsById,
      restoreProcurementRowsLocally,
      rowsData,
    ],
  );

  const handleSocketSubmitForApproval = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      if (
        !doesProcurementSocketMatchCurrentProject(
          socketMessage,
          null,
          company_id,
          id,
        )
      ) {
        return;
      }

      const pendingEntryIds =
        getProcurementSocketPendingApprovalEntryIds(socketMessage);

      if (!pendingEntryIds.length) {
        return;
      }

      pulseIncomingSyncActivity("Receiving live updates");
      setDeletedEntryIds((previousEntryIds) =>
        previousEntryIds.filter(
          (entryId) => !pendingEntryIds.includes(String(entryId)),
        ),
      );
      setLiveItemsPendingApprovals((previousEntryIds) => {
        const nextEntryIdSet = new Set((previousEntryIds || []).map(String));

        pendingEntryIds.forEach((entryId) => {
          nextEntryIdSet.add(String(entryId));
        });

        const nextEntryIds = [...nextEntryIdSet];

        return nextEntryIds.length === (previousEntryIds || []).length
          ? previousEntryIds
          : nextEntryIds;
      });
    },
    [company_id, id, pulseIncomingSyncActivity],
  );

  const handleSocketApprovalDecision = useCallback(
    (message) => {
      const socketMessage = getProcurementSocketEnvelope(message);

      if (
        !doesProcurementSocketMatchCurrentProject(
          socketMessage,
          null,
          company_id,
          id,
        )
      ) {
        return;
      }

      const decisionRows = Array.isArray(socketMessage?.data)
        ? socketMessage.data.filter(
            (row) => row && typeof row === "object" && !Array.isArray(row),
          )
        : [];

      if (!decisionRows.length) {
        return;
      }

      const matchingDecisionRows = decisionRows.filter((decisionRow) =>
        doesProcurementSocketMatchCurrentProject(
          socketMessage,
          decisionRow,
          company_id,
          id,
        ),
      );

      if (!matchingDecisionRows.length) {
        return;
      }

      const resolvedEntryIds = [
        ...new Set(
          matchingDecisionRows
            .map((decisionRow) => getProcurementSocketEntryId(decisionRow))
            .filter(Boolean)
            .map(String),
        ),
      ];

      if (resolvedEntryIds.length) {
        pulseIncomingSyncActivity("Receiving live updates");
        setLiveItemsPendingApprovals((previousEntryIds) => {
          const nextEntryIds = (previousEntryIds || []).filter(
            (entryId) => !resolvedEntryIds.includes(String(entryId)),
          );

          return nextEntryIds.length === (previousEntryIds || []).length
            ? previousEntryIds
            : nextEntryIds;
        });
      }

      setGridRowsData((previousRows) => {
        const previousRowsById = new Map(
          previousRows.map((row, index) => [
            getProcurementRowId(row, index),
            row,
          ]),
        );
        const rowPatches = new Map();

        matchingDecisionRows.forEach((decisionRow) => {
          const entryId = getProcurementSocketEntryId(decisionRow);

          if (!entryId || !previousRowsById.has(String(entryId))) {
            return;
          }

          const normalizedDecision = normalizeText(
            decisionRow?.decision,
          ).toLowerCase();
          const isApprovedDecision =
            normalizedDecision === "approve" ||
            normalizedDecision === "approved" ||
            normalizedDecision === "accept" ||
            normalizedDecision === "accepted";

          if (!isApprovedDecision) {
            return;
          }

          const approvalType = normalizeText(
            decisionRow?.approvalType,
          ).toLowerCase();
          const nextPatch = { ...(rowPatches.get(String(entryId)) || {}) };

          if (approvalType === "leadtime") {
            nextPatch.leadTime = firstFilled(decisionRow?.newLeadTime, "");
          }

          if (
            approvalType === "subcommitted" ||
            approvalType === "subcommitteddeliverydate"
          ) {
            nextPatch.subCommittedDeliveryDate = firstFilled(
              decisionRow?.newSubCommittedDeliveryDate,
              "",
            );
          }

          if (Object.keys(nextPatch).length) {
            rowPatches.set(String(entryId), nextPatch);
          }
        });

        if (!rowPatches.size) {
          return previousRows;
        }

        const { recalculatedRows, changedRowUpdates } = applyLocalRowPatches({
          previousRows,
          rowPatches,
          formulaColumns,
          alignedData,
          scheduleSyncContext,
          settingsInfo,
          overwriteCalculations: groupingSettings.overwriteCalculations,
          businessCalendarContext,
          markEdited: false,
        });

        const flashEntries = changedRowUpdates
          .map((changedRowUpdate) => ({
            entryId: String(changedRowUpdate.entryID),
            columnIds: Object.keys(changedRowUpdate).filter(
              (key) => key !== "entryID",
            ),
          }))
          .filter(({ columnIds }) => columnIds.length > 0);

        if (flashEntries.length) {
          queueProcurementCellFlash(flashEntries);
        }

        gridRowsDataRef.current = recalculatedRows;
        return recalculatedRows;
      });
    },
    [
      alignedData,
      businessCalendarContext,
      company_id,
      formulaColumns,
      groupingSettings.overwriteCalculations,
      id,
      pulseIncomingSyncActivity,
      queueProcurementCellFlash,
      scheduleSyncContext,
      settingsInfo,
    ],
  );

  // Keeps the latest socket handlers available without reconnecting the socket whenever page state changes.
  useEffect(() => {
    handleSocketInsertedRowRef.current = handleSocketInsertedRow;
    handleSocketDeletedRowRef.current = handleSocketDeletedRow;
    handleSocketRestoredDeletedRowRef.current = handleSocketRestoredDeletedRow;
    handleSocketCriticalPathRowRef.current = handleSocketCriticalPathRow;
    handleSocketTaskAssignmentRef.current = handleSocketTaskAssignment;
    handleSocketEditedRowRef.current = handleSocketEditedRow;
    handleSocketPostToProcoreRef.current = handleSocketPostToProcore;
    handleSocketPendingApprovalRef.current = handleSocketSubmitForApproval;
    handleSocketApprovalDecisionRef.current = handleSocketApprovalDecision;
    handleSocketUserInCellRef.current = handleSocketUserInCell;
    handleSocketUserOutOfCellRef.current = handleSocketUserOutOfCell;
  }, [
    handleSocketCriticalPathRow,
    handleSocketApprovalDecision,
    handleSocketDeletedRow,
    handleSocketEditedRow,
    handleSocketInsertedRow,
    handleSocketPostToProcore,
    handleSocketSubmitForApproval,
    handleSocketRestoredDeletedRow,
    handleSocketTaskAssignment,
    handleSocketUserInCell,
    handleSocketUserOutOfCell,
  ]);

  // Clears any pending remote edit batch timer when the page unmounts.
  useEffect(() => {
    return () => {
      clearIncomingSyncTimeout();
      clearRemoteEditingPresence();

      if (
        pendingSocketEditFlushTimeoutRef.current !== null &&
        typeof window !== "undefined"
      ) {
        window.clearTimeout(pendingSocketEditFlushTimeoutRef.current);
      }

      if (localEditingCellRef.current) {
        const code = AuthStorage.getItem("token");
        const loginEmail = AuthStorage.getItem("email");

        ProjectServices.userOutOfCell(
          {
            entryID: localEditingCellRef.current.entryID,
            columnName: localEditingCellRef.current.columnName,
          },
          {
            company_id,
            project_id: id,
            code,
            loginEmail,
          },
        ).catch(() => {});

        localEditingCellRef.current = null;
      }
    };
  }, [clearIncomingSyncTimeout, clearRemoteEditingPresence, company_id, id]);

  // Connects the beta procurement log to the backend socket and logs status/data exactly like the backend team's test file.
  useEffect(() => {
    let isCancelled = false;
    let socketInstance = null;

    loadSocketIoClientScript()
      .then((ioFactory) => {
        if (isCancelled) {
          return;
        }

        const backendUrl = getProcurementSocketBackendUrl();
        socketInstance = ioFactory(backendUrl);
        procurementSocketRef.current = socketInstance;

        console.log("BACKEND_URL:", backendUrl);

        socketInstance.on("connect", () => {
          console.log("Socket connected:", socketInstance.id);
          clearSocketSyncErrorState();
          socketInstance.emit("join", { companyID: company_id, projectID: id });
          console.log(`Joined room: ${company_id}_${id}`);
        });

        socketInstance.on("connect_error", (error) => {
          console.log("Socket connection error:", error);
          setSocketSyncErrorState();
        });

        socketInstance.on("disconnect", (reason) => {
          if (isCancelled) {
            return;
          }

          console.log("Socket disconnected:", reason);
          clearRemoteEditingPresence();
          setSocketSyncErrorState();
        });

        socketInstance.onAny((eventName, ...args) => {
          console.log("Socket event:", eventName, args);
        });

        socketInstance.on("procurement_log_insert", (message) => {
          handleSocketInsertedRowRef.current?.(message);
        });

        socketInstance.on("procurement_log_delete", (message) => {
          handleSocketDeletedRowRef.current?.(message);
        });

        socketInstance.on("procurement_log_hide", (message) => {
          handleSocketDeletedRowRef.current?.(message, false);
        });

        socketInstance.on("procurement_log_edit", (message) => {
          handleSocketEditedRowRef.current?.(message);
        });

        socketInstance.on("procurement_log_post_to_procore", (message) => {
          handleSocketPostToProcoreRef.current?.(message);
        });

        socketInstance.on("procurement_log_restore_deleted", (message) => {
          handleSocketRestoredDeletedRowRef.current?.(message);
        });

        socketInstance.on("procurement_log_restore_hidden", (message) => {
          handleSocketRestoredDeletedRowRef.current?.(message);
        });

        socketInstance.on("procurement_log_critical_path", (message) => {
          handleSocketCriticalPathRowRef.current?.(message, true);
        });

        socketInstance.on("procurement_log_unflag_critical_path", (message) => {
          handleSocketCriticalPathRowRef.current?.(message, false);
        });

        socketInstance.on("procurement_log_task_assigned", (message) => {
          handleSocketTaskAssignmentRef.current?.(message, true);
        });

        socketInstance.on("procurement_log_task_deleted", (message) => {
          handleSocketTaskAssignmentRef.current?.(message, false);
        });

        socketInstance.on("procurement_log_submit_for_approval", (message) => {
          handleSocketPendingApprovalRef.current?.(message);
        });

        socketInstance.on("procurement_log_approval_decision", (message) => {
          handleSocketApprovalDecisionRef.current?.(message);
        });

        socketInstance.on("procurement_log_user_in_cell", (message) => {
          handleSocketUserInCellRef.current?.(message);
        });

        socketInstance.on("procurement_log_user_out_of_cell", (message) => {
          handleSocketUserOutOfCellRef.current?.(message);
        });
      })
      .catch((error) => {
        console.error("Failed to initialize procurement socket", error);
        setSocketSyncErrorState();
      });

    return () => {
      isCancelled = true;

      if (socketInstance) {
        socketInstance.disconnect();
      }

      procurementSocketRef.current = null;
    };
  }, [
    clearRemoteEditingPresence,
    clearSocketSyncErrorState,
    company_id,
    id,
    setSocketSyncErrorState,
  ]);

  const dependencyStateByEntryId = useMemo(
    () => buildDependencyStateByEntryId(dependencyGraph, gridRowsData),
    [dependencyGraph, gridRowsData],
  );

  // Turns off the accessible-only filter when the user is no longer in assigned-item mode.
  useEffect(() => {
    if (accessLevel > 1) {
      setShowAccessibleOnly(false);
    }
  }, [accessLevel]);

  useEffect(() => {
    setAdvancedFilters((previousState) => {
      const nextAssignedTo = previousState.assignedTo.filter(
        (email) => liveTasksDict?.[email],
      );

      if (nextAssignedTo.length === previousState.assignedTo.length) {
        return previousState;
      }

      return {
        ...previousState,
        assignedTo: nextAssignedTo,
      };
    });
  }, [liveTasksDict]);

  const filteredRowsData = useMemo(() => {
    const accessibleRows =
      showAccessibleOnly && accessLevel < 2
        ? filterRowsByAccessibleItems(
            gridRowsData,
            columnPermissions.selectableEntryIds,
          )
        : gridRowsData;
    const afterSearchFilter = filterRowsBySubmittalSearch(
      accessibleRows,
      searchValue,
    );
    const afterAdvancedFilters = filterRowsByAdvancedFilters(
      afterSearchFilter,
      advancedFilters,
      liveTasksDict,
      groupingSettings.nestSubmittals,
    );
    return filterRowsByColumnFilters(
      afterAdvancedFilters,
      columnFilterModel,
      alignedData,
    );
  }, [
    accessLevel,
    alignedData,
    advancedFilters,
    columnFilterModel,
    columnPermissions.selectableEntryIds,
    gridRowsData,
    searchValue,
    showAccessibleOnly,
    liveTasksDict,
    groupingSettings.nestSubmittals,
  ]);

  const submittalsTotalCount = useMemo(
    () => gridRowsData.length,
    [gridRowsData],
  );

  const activeFilterCount = useMemo(
    () =>
      Object.keys(columnFilterModel).length +
      (advancedFilters.statuses.length > 0 ? 1 : 0) +
      (advancedFilters.assignedTo.length > 0 ? 1 : 0) +
      (advancedFilters.entryOrigins.length > 0 ? 1 : 0),
    [
      advancedFilters.assignedTo.length,
      advancedFilters.statuses.length,
      advancedFilters.entryOrigins.length,
      columnFilterModel,
    ],
  );

  const handleClearActiveFilters = useCallback(() => {
    setColumnFilterModel({});
    setAdvancedFilters({ statuses: [], assignedTo: [], entryOrigins: [] });
  }, []);

  const hasActiveSearch = useMemo(
    () => Boolean(normalizeText(searchValue)),
    [searchValue],
  );

  const hasActiveTreeFilters = useMemo(
    () => hasActiveSearch || activeFilterCount > 0,
    [activeFilterCount, hasActiveSearch],
  );

  const selectionTreeModel = useMemo(
    () => buildProcurementTree(gridRowsData, divisions, subdivisions),
    [gridRowsData, divisions, subdivisions],
  );

  const selectionDescendantsLookup = useMemo(
    () => buildSelectionDescendantsLookup(selectionTreeModel),
    [selectionTreeModel],
  );

  // Maps access-limited users to the specific submittal row IDs they are allowed to select.
  const selectableSubmittalRowIds = useMemo(() => {
    if (columnPermissions.accessLevel > 1) {
      return null;
    }

    const allowedEntryIds = columnPermissions.selectableEntryIds;
    if (!(allowedEntryIds instanceof Set)) {
      return new Set();
    }

    return new Set(
      (gridRowsData || [])
        .filter((row) => allowedEntryIds.has(String(row?.entryID)))
        .map((row, index) => `submittal:${getProcurementRowId(row, index)}`),
    );
  }, [
    columnPermissions.accessLevel,
    columnPermissions.selectableEntryIds,
    gridRowsData,
  ]);

  // Builds the project-details shape expected by legacy bulk action components.
  const projectDetails = useMemo(
    () => ({
      content: selectionTreeModel.reduce((accumulator, division) => {
        accumulator[division.displayTitle] = division.subdivisions.reduce(
          (subdivisionAccumulator, subdivision) => {
            subdivisionAccumulator[subdivision.displayTitle] =
              subdivision.items.map(({ __rowId, __sourceIndex, ...row }) => ({
                ...row,
              }));
            return subdivisionAccumulator;
          },
          {},
        );
        return accumulator;
      }, {}),
      usersInfo: liveUsersInfo,
      nonWorkingDays,
      viewsInfo: {
        alignedData,
      },
    }),
    [selectionTreeModel, liveUsersInfo, nonWorkingDays, alignedData],
  );

  // Resolves the currently opened assign-drawer row from the live grid data.
  const assignDrawerRow = useMemo(
    () =>
      gridRowsData.find(
        (row, index) => getProcurementRowId(row, index) === assignDrawerRowId,
      ) || null,
    [assignDrawerRowId, gridRowsData],
  );

  const displayedAssignDrawerRow = assignDrawerRow || assignDrawerSnapshotRow;

  // Keeps the drawer content mounted with the latest row snapshot until the close animation finishes.
  useEffect(() => {
    if (assignDrawerRow && isAssignDrawerOpen) {
      setAssignDrawerSnapshotRow(assignDrawerRow);
    }
  }, [assignDrawerRow, isAssignDrawerOpen]);

  // Closes the assign drawer automatically if its backing row disappears from the grid.
  useEffect(() => {
    if (assignDrawerRowId && !assignDrawerRow) {
      setIsAssignDrawerOpen(false);
    }
  }, [assignDrawerRow, assignDrawerRowId]);

  const legacyProjectBasePath = useMemo(
    () => `/main/company/${company_id}/projects/${id}`,
    [company_id, id],
  );

  const treeModel = useMemo(
    () =>
      buildProcurementTree(filteredRowsData, divisions, subdivisions, {
        includeEmptyGroups: !hasActiveTreeFilters,
      }),
    [filteredRowsData, divisions, subdivisions, hasActiveTreeFilters],
  );

  const exportRows = useMemo(() => {
    const expandedDivisionState = (treeModel || []).reduce(
      (accumulator, division) => {
        accumulator[division.key] = true;
        return accumulator;
      },
      {},
    );
    const expandedSubdivisionState = (treeModel || []).reduce(
      (accumulator, division) => {
        (division.subdivisions || []).forEach((subdivision) => {
          accumulator[subdivision.key] = true;
        });
        return accumulator;
      },
      {},
    );

    return flattenProcurementRows(
      treeModel,
      expandedDivisionState,
      expandedSubdivisionState,
      sortModel,
      alignedData,
      groupingSettings.nestSubmittals,
      false,
    );
  }, [treeModel, sortModel, alignedData, groupingSettings.nestSubmittals]);

  const divisionKeys = useMemo(
    () => treeModel.map((division) => division.key),
    [treeModel],
  );

  const subdivisionKeys = useMemo(
    () =>
      treeModel.flatMap((division) =>
        division.subdivisions.map((subdivision) => subdivision.key),
      ),
    [treeModel],
  );

  const normalizedExpandedDivisions = useMemo(
    () => reconcileExpandedState(expandedDivisions, divisionKeys, false),
    [expandedDivisions, divisionKeys],
  );

  const normalizedExpandedSubdivisions = useMemo(
    () => reconcileExpandedState(expandedSubdivisions, subdivisionKeys, false),
    [expandedSubdivisions, subdivisionKeys],
  );

  useEffect(() => {
    const selectableIds = new Set(selectionDescendantsLookup.keys());

    setSelectedIds((previousState) => {
      let hasChanged = false;
      const nextState = new Set();

      previousState.forEach((selectedId) => {
        if (selectableIds.has(selectedId)) {
          nextState.add(selectedId);
          return;
        }

        hasChanged = true;
      });

      return hasChanged ? nextState : previousState;
    });
  }, [selectionDescendantsLookup]);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  useEffect(() => {
    if (hasActiveTreeFilters && !wasTreePrunedRef.current) {
      preFilteredExpandedDivisionsRef.current = { ...expandedDivisions };
      preFilteredExpandedSubdivisionsRef.current = {
        ...expandedSubdivisions,
      };
      wasTreePrunedRef.current = true;
      return;
    }

    if (!hasActiveTreeFilters && wasTreePrunedRef.current) {
      const previousExpandedDivisions =
        preFilteredExpandedDivisionsRef.current || {};
      const previousExpandedSubdivisions =
        preFilteredExpandedSubdivisionsRef.current || {};

      setExpandedDivisions(() =>
        reconcileExpandedState(previousExpandedDivisions, divisionKeys, false),
      );
      setExpandedSubdivisions(() =>
        reconcileExpandedState(
          previousExpandedSubdivisions,
          subdivisionKeys,
          false,
        ),
      );

      preFilteredExpandedDivisionsRef.current = null;
      preFilteredExpandedSubdivisionsRef.current = null;
      wasTreePrunedRef.current = false;
    }
  }, [
    divisionKeys,
    expandedDivisions,
    expandedSubdivisions,
    hasActiveTreeFilters,
    subdivisionKeys,
  ]);

  useEffect(() => {
    setExpandedDivisions((previousState) =>
      reconcileExpandedState(previousState, divisionKeys, false),
    );
    setExpandedSubdivisions((previousState) =>
      reconcileExpandedState(previousState, subdivisionKeys, false),
    );
  }, [divisionKeys, subdivisionKeys]);

  useEffect(() => {
    if (!normalizeText(searchValue)) {
      return;
    }

    setExpandedDivisions((previousState) => {
      const nextState = { ...previousState };
      let hasChanged = false;

      divisionKeys.forEach((key) => {
        if (nextState[key] !== true) {
          nextState[key] = true;
          hasChanged = true;
        }
      });

      return hasChanged ? nextState : previousState;
    });

    setExpandedSubdivisions((previousState) => {
      const nextState = { ...previousState };
      let hasChanged = false;

      subdivisionKeys.forEach((key) => {
        if (nextState[key] !== true) {
          nextState[key] = true;
          hasChanged = true;
        }
      });

      return hasChanged ? nextState : previousState;
    });
  }, [searchValue, divisionKeys, subdivisionKeys]);

  const allExpanded = useMemo(() => {
    const allKeys = [...divisionKeys, ...subdivisionKeys];

    if (allKeys.length === 0) {
      return true;
    }

    return allKeys.every((key) => {
      if (divisionKeys.includes(key)) {
        return normalizedExpandedDivisions[key] ?? false;
      }

      return normalizedExpandedSubdivisions[key] ?? false;
    });
  }, [
    divisionKeys,
    subdivisionKeys,
    normalizedExpandedDivisions,
    normalizedExpandedSubdivisions,
  ]);

  // Toggles a division or subdivision row open and closed.
  const handleToggleGroup = useCallback((row) => {
    if (row.rowType === "division") {
      setExpandedDivisions((previousState) => ({
        ...previousState,
        [row.groupKey]: !(previousState[row.groupKey] ?? false),
      }));
      return;
    }

    if (row.rowType === "subdivision") {
      setExpandedSubdivisions((previousState) => ({
        ...previousState,
        [row.groupKey]: !(previousState[row.groupKey] ?? false),
      }));
    }
  }, []);

  // Expands or collapses every visible division and subdivision at once.
  const handleToggleAllGroups = useCallback(() => {
    const nextExpanded = !allExpanded;

    setExpandedDivisions(
      divisionKeys.reduce((accumulator, key) => {
        accumulator[key] = nextExpanded;
        return accumulator;
      }, {}),
    );

    setExpandedSubdivisions(
      subdivisionKeys.reduce((accumulator, key) => {
        accumulator[key] = nextExpanded;
        return accumulator;
      }, {}),
    );
  }, [allExpanded, divisionKeys, subdivisionKeys]);

  // Cycles the active column sort while preserving the tree structure.
  const handleToggleSort = useCallback((colId) => {
    if (!colId) {
      return;
    }

    setSortModel((previousSortModel) => {
      if (!previousSortModel || previousSortModel.colId !== colId) {
        return { colId, sort: "asc" };
      }

      if (previousSortModel.sort === "asc") {
        return { colId, sort: "desc" };
      }

      return null;
    });
  }, []);

  const handleSetColumnFilter = useCallback((field, filterCondition) => {
    setColumnFilterModel((prev) => {
      if (!filterCondition) {
        // Remove the key entirely so Object.keys().length stays accurate
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: filterCondition };
    });
  }, []);

  // Collects every selectable descendant ID for a given tree row.
  const getDescendantIds = useCallback(
    (rowId, rowType) => {
      if (!rowId || rowType === "subdivisionAddRow") {
        return new Set();
      }

      return (
        selectionDescendantsLookup.get(rowId) || new Set(rowType ? [rowId] : [])
      );
    },
    [selectionDescendantsLookup],
  );

  // Filters a row's selection scope down to what the current user is actually allowed to select.
  const getSelectableIds = useCallback(
    (rowId, rowType) => {
      const descendants = getDescendantIds(rowId, rowType);

      if (columnPermissions.accessLevel > 1) {
        return descendants;
      }

      if (!descendants.size || !selectableSubmittalRowIds) {
        return new Set();
      }

      return new Set(
        [...descendants].filter(
          (descendantId) =>
            descendantId.startsWith("submittal:") &&
            selectableSubmittalRowIds.has(descendantId),
        ),
      );
    },
    [
      columnPermissions.accessLevel,
      getDescendantIds,
      selectableSubmittalRowIds,
    ],
  );

  // Determines whether a row's checkbox should be disabled for the current user.
  const isSelectionDisabled = useCallback(
    (rowId, rowType) => getSelectableIds(rowId, rowType).size === 0,
    [getSelectableIds],
  );

  // Applies smart selection to a row and its related descendants.
  const handleRowSelect = useCallback(
    (rowId, rowType) => {
      const descendants = getSelectableIds(rowId, rowType);

      if (!descendants.size) {
        return;
      }

      setSelectedIds((previousState) => {
        const nextState = new Set(previousState);
        const allSelected = [...descendants].every((id) => nextState.has(id));

        if (allSelected) {
          descendants.forEach((id) => nextState.delete(id));
        } else {
          descendants.forEach((id) => nextState.add(id));
        }

        return nextState;
      });
    },
    [getSelectableIds],
  );

  // Resolves whether a row checkbox is unchecked, partial, or fully selected.
  const getCheckState = useCallback(
    (rowId, rowType) => {
      const descendants = getSelectableIds(rowId, rowType);

      if (!descendants.size) {
        return "none";
      }

      const currentSelectedCount = [...descendants].filter((selectedId) =>
        selectedIdsRef.current.has(selectedId),
      ).length;

      if (currentSelectedCount === 0) {
        return "none";
      }

      if (currentSelectedCount === descendants.size) {
        return "all";
      }

      return "some";
    },
    [getSelectableIds],
  );

  // Removes stale or no-longer-selectable rows from the smart selection state.
  useEffect(() => {
    if (columnPermissions.accessLevel > 1) {
      return;
    }

    setSelectedIds((previousState) => {
      const nextState = new Set(
        [...previousState].filter((selectedId) =>
          selectableSubmittalRowIds?.has(selectedId),
        ),
      );

      return nextState.size === previousState.size ? previousState : nextState;
    });
  }, [columnPermissions.accessLevel, selectableSubmittalRowIds]);

  // Converts the current smart selection into the legacy checkedRows map shape.
  const checkedRows = useMemo(
    () =>
      Array.from(selectedIds).reduce((accumulator, selectedId) => {
        if (!selectedId.startsWith("submittal:")) {
          return accumulator;
        }

        accumulator[selectedId.replace("submittal:", "")] = true;
        return accumulator;
      }, {}),
    [selectedIds],
  );

  // Counts only selected submittal rows for the quick-bar badge and bulk actions.
  const selectedCount = useMemo(
    () =>
      Array.from(selectedIds).filter((selectedId) =>
        selectedId.startsWith("submittal:"),
      ).length,
    [selectedIds],
  );
  const canCreatePurchaseOrder = useMemo(() => {
    if (accessLevel < 2) {
      return false;
    }

    if (Number.isFinite(wareHouseAccessLevel)) {
      return wareHouseAccessLevel >= 3;
    }

    return warehouseSubscription === 1;
  }, [accessLevel, wareHouseAccessLevel, warehouseSubscription]);
  const isSubscribedToWarehouse = useMemo(
    () => warehouseSubscription === 1,
    [warehouseSubscription],
  );
  const canOpenWarehouseTransfers = useMemo(
    () => accessLevel >= 2 && !isLoading,
    [accessLevel, isLoading],
  );

  // Loads the same warehouse transfer history used in the legacy procurement log modal.
  const transfersToProjectInventoryHistory = useCallback(() => {
    setIsitemsShippedLoading(true);

    const code = AuthStorage.getItem("token");
    const email = AuthStorage.getItem("email");
    const requestData = {
      company: company_id,
      projectID: id,
      code,
      email,
    };

    WarehouseServices.transfersToProjectInventoryHistory(requestData)
      .then((response) => {
        setItemsShippedData(response?.inProcessingTransfers || []);
        setItemsShippedHistoryData(response?.completedTransfers || []);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsitemsShippedLoading(false);
      });
  }, [company_id, id]);

  // Applies legacy checkedRows updates back into the new smart-selection state.
  const setCheckedRows = useCallback((nextCheckedRows) => {
    if (Array.isArray(nextCheckedRows) || !nextCheckedRows) {
      setSelectedIds(new Set());
      return;
    }

    const nextSelectedIds = new Set();
    Object.entries(nextCheckedRows).forEach(([entryID, isChecked]) => {
      if (isChecked) {
        nextSelectedIds.add(`submittal:${entryID}`);
      }
    });
    setSelectedIds(nextSelectedIds);
  }, []);

  const treeRows = useMemo(
    () =>
      flattenProcurementRows(
        treeModel,
        normalizedExpandedDivisions,
        normalizedExpandedSubdivisions,
        sortModel,
        alignedData,
        groupingSettings.nestSubmittals,
        accessLevel >= 2,
      ),
    [
      treeModel,
      normalizedExpandedDivisions,
      normalizedExpandedSubdivisions,
      sortModel,
      alignedData,
      groupingSettings.nestSubmittals,
      accessLevel,
    ],
  );

  const positionStorageKey = useMemo(
    () =>
      `procurement-log-position:${JSON.stringify([
        AuthStorage.getItem("email"),
        company_id,
        id,
        lastUsedView,
      ])}`,
    [company_id, id, lastUsedView],
  );
  const positionViewState = useMemo(
    () => ({
      expandedDivisions,
      expandedSubdivisions,
      searchValue,
      showAccessibleOnly,
      sortModel,
      columnFilterModel,
      advancedFilters,
    }),
    [
      expandedDivisions,
      expandedSubdivisions,
      searchValue,
      showAccessibleOnly,
      sortModel,
      columnFilterModel,
      advancedFilters,
    ],
  );
  const restorePositionViewState = useCallback((savedState) => {
    setExpandedDivisions(savedState.expandedDivisions || {});
    setExpandedSubdivisions(savedState.expandedSubdivisions || {});
    setSearchValue(savedState.searchValue || "");
    setShowAccessibleOnly(savedState.showAccessibleOnly || false);
    setSortModel(savedState.sortModel || null);
    setColumnFilterModel(savedState.columnFilterModel || {});
    setAdvancedFilters({
      statuses: savedState.advancedFilters?.statuses || [],
      assignedTo: savedState.advancedFilters?.assignedTo || [],
      entryOrigins: savedState.advancedFilters?.entryOrigins || [],
    });
    preFilteredExpandedDivisionsRef.current = null;
    preFilteredExpandedSubdivisionsRef.current = null;
    wasTreePrunedRef.current = false;
  }, []);
  const { handleBodyScroll, handleGridRowsReady, handleGridPreDestroyed } =
    useProcurementLogPosition({
      storageKey: positionStorageKey,
      ready:
        !isLoading &&
        !loadError &&
        Boolean(alignedData) &&
        rowsData === appliedRowsDataRef.current,
      gridRef,
      rows: treeRows,
      viewState: positionViewState,
      onRestoreViewState: restorePositionViewState,
    });

  // Toggles a single submittal between flagged and unflagged critical path state.
  const handleToggleCriticalPath = useCallback(
    async (row) => {
      const entryID = firstFilled(row?.entryID, row?.tempId, row?.id);

      if (!entryID || criticalPathRequestIdsRef.current.has(entryID)) {
        return;
      }

      criticalPathRequestIdsRef.current.add(entryID);

      const isCurrentlyFlagged =
        normalizeText(row?.criticalPathFlag).toLowerCase() === "true";
      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");
      const requestData = {
        company_id,
        id,
        code,
        loginEmail,
        entryID: [entryID],
      };
      const syncToken = beginSyncActivity(
        isCurrentlyFlagged
          ? "Removing submittal from critical path"
          : "Adding submittal to critical path",
      );

      try {
        if (isCurrentlyFlagged) {
          await CriticalPathFlagItemsService.unflagCriticalPathEntriesFlag(
            requestData,
          );
        } else {
          await CriticalPathFlagItemsService.procurementLogFlagCriticalPathEntry(
            requestData,
          );
        }

        setProcurementCriticalPathState([entryID], !isCurrentlyFlagged);
        finishSyncActivity(syncToken);
      } catch (error) {
        console.error("Failed to toggle critical path state", error);
        failSyncActivity(
          syncToken,
          "Failed to update critical path. You may no longer be synced to the latest version.",
        );
      } finally {
        criticalPathRequestIdsRef.current.delete(entryID);
      }
    },
    [
      beginSyncActivity,
      company_id,
      failSyncActivity,
      finishSyncActivity,
      id,
      setProcurementCriticalPathState,
    ],
  );

  // Removes deleted rows from the local grid immediately after a successful bulk delete.
  const handleBulkDeleteSuccess = useCallback(
    (deletedEntryIds) => {
      removeProcurementRowsById(deletedEntryIds, {
        clearSelection: true,
        removePendingApprovals: true,
      });
    },
    [removeProcurementRowsById],
  );

  // Deletes non-Procore rows or hides Procore rows from the table and backend.
  const deleteOrHideRow = useCallback(
    async (row) => {
      const rowIdentity = firstFilled(row?.entryID, row?.tempId, row?.id);

      if (!rowIdentity || rowActionRequestIdsRef.current.has(rowIdentity)) {
        return;
      }

      rowActionRequestIdsRef.current.add(rowIdentity);

      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");
      const isProcoreEntry = row?.entryOrigin === "Procore";
      const syncToken = beginSyncActivity(
        isProcoreEntry ? "Hiding submittal" : "Deleting submittal",
      );

      try {
        if (isProcoreEntry) {
          await HiddenItemsService.procurementLogHideEntry({
            company_id,
            id,
            code,
            loginEmail,
            entryID: [rowIdentity],
          });
        } else {
          const isLocalOnlyRow =
            row?.isNew === "true" || String(rowIdentity).startsWith("new-");

          if (!isLocalOnlyRow) {
            await ProjectServices.deleteRow(
              { entryID: [rowIdentity] },
              {
                company_id,
                project_id: id,
                code,
                loginEmail,
              },
            );
          }
        }

        removeProcurementRowsById([rowIdentity], {
          clearSelection: true,
          removePendingApprovals: !isProcoreEntry,
        });
        finishSyncActivity(syncToken);
      } catch (error) {
        console.error("Failed to remove procurement row", error);
        failSyncActivity(
          syncToken,
          isProcoreEntry
            ? "Failed to hide submittal. You may no longer be synced to the latest version."
            : "Failed to delete submittal. You may no longer be synced to the latest version.",
        );
      } finally {
        rowActionRequestIdsRef.current.delete(rowIdentity);
      }
    },
    [
      beginSyncActivity,
      company_id,
      failSyncActivity,
      finishSyncActivity,
      id,
      removeProcurementRowsById,
    ],
  );

  // Warns before deleting a submittal that has pending approval requests.
  const handleDeleteOrHideRow = useCallback(
    (row) => {
      const rowIdentity = firstFilled(row?.entryID, row?.tempId, row?.id);
      const isPendingApproval = liveItemsPendingApprovals.some(
        (entryId) => String(entryId) === rowIdentity,
      );

      if (row?.entryOrigin !== "Procore" && isPendingApproval) {
        Modal.confirm({
          title: "Delete item pending approval?",
          content:
            "This item is pending approval. Deleting it will also remove it from the pending approvals list.",
          okText: "Delete",
          okButtonProps: { danger: true },
          cancelText: "Cancel",
          centered: true,
          onOk: () => deleteOrHideRow(row),
        });
        return;
      }

      return deleteOrHideRow(row);
    },
    [deleteOrHideRow, liveItemsPendingApprovals],
  );

  // Opens the right-side assignment drawer for the clicked submittal row.
  const handleOpenAssignDrawer = useCallback((row) => {
    const nextRowId = firstFilled(row?.entryID, row?.tempId, row?.id);

    if (!nextRowId) {
      return;
    }

    setAssignDrawerSnapshotRow(row);
    setAssignDrawerRowId(nextRowId);
    setIsAssignDrawerOpen(true);
  }, []);

  // Creates a server-backed row first, then applies the cloned row data to its new entry ID.
  const handleCloneSubmittal = useCallback(
    async (sourceRow) => {
      const sourceRowId = firstFilled(
        sourceRow?.entryID,
        sourceRow?.tempId,
        sourceRow?.id,
      );
      const requestKey = `clone:${sourceRowId}`;

      if (!sourceRowId || rowActionRequestIdsRef.current.has(requestKey)) {
        return;
      }

      const divisionTitle = firstFilled(
        sourceRow?.divisionID,
        sourceRow?.divisionId,
        sourceRow?.div,
        sourceRow?.division,
        sourceRow?.divisionName,
      );
      const subdivisionTitle = firstFilled(
        sourceRow?.subdivisionID,
        sourceRow?.subdivisionId,
        sourceRow?.subDiv,
        sourceRow?.subdivision,
        sourceRow?.subdivisionName,
        sourceRow?.specSection,
      );

      if (!subdivisionTitle) {
        return;
      }

      rowActionRequestIdsRef.current.add(requestKey);
      setCloningEntryIds((previousEntryIds) => {
        const nextEntryIds = new Set(previousEntryIds);
        nextEntryIds.add(String(sourceRowId));
        return nextEntryIds;
      });
      const syncToken = beginSyncActivity("Cloning submittal");
      let clonedEntryId = "";

      try {
        const draftRow = await createNewSubmittalDraft(
          alignedData,
          {
            divisionDisplayTitle: divisionTitle,
            displayTitle: subdivisionTitle,
          },
          company_id,
          id,
        );
        clonedEntryId = firstFilled(draftRow?.entryID);

        if (!clonedEntryId || String(clonedEntryId).startsWith("new-")) {
          throw new Error("The new submittal did not receive a server entry ID.");
        }

        const cloneParentId = groupingSettings.nestSubmittals
          ? getProcurementFamilyRootId(
              sourceRow,
              gridRowsDataRef.current,
            )
          : "";
        const clonedRowPatch = buildClonedRowPatch(
          sourceRow,
          alignedData,
          cloneParentId,
        );
        const code = AuthStorage.getItem("token");
        const loginEmail = AuthStorage.getItem("email");

        await ProjectServices.procurementLogEditSingleColumn(
          {
            entryID: clonedEntryId,
            ...clonedRowPatch,
          },
          {
            company: company_id,
            project: id,
            company_id,
            project_id: id,
            code,
            loginEmail,
          },
        );

        const clonedRow = {
          ...draftRow,
          ...clonedRowPatch,
          entryID: clonedEntryId,
          tempId: clonedEntryId,
          id: `submittal:${clonedEntryId}`,
          isNew: "false",
        };

        setGridRowsData((previousRows) => {
          const recalculatedRows = applyFormulaColumnsToRows(
            [...previousRows, clonedRow],
            formulaColumns,
            alignedData,
            scheduleSyncContext,
            settingsInfo,
            businessCalendarContext,
          );

          gridRowsDataRef.current = recalculatedRows;
          return recalculatedRows;
        });

        requestAnimationFrame(() => {
          const rowNode = gridRef.current?.api?.getRowNode(
            `submittal:${clonedEntryId}`,
          );

          if (rowNode) {
            gridRef.current?.api?.ensureNodeVisible(rowNode, "middle");
          }
        });
        finishSyncActivity(syncToken);
      } catch (error) {
        console.error("Failed to clone procurement submittal", error);
        failSyncActivity(
          syncToken,
          "Failed to clone submittal. You may no longer be synced to the latest version.",
        );

        if (clonedEntryId) {
          reloadData();
        }
      } finally {
        rowActionRequestIdsRef.current.delete(requestKey);
        setCloningEntryIds((previousEntryIds) => {
          if (!previousEntryIds.has(String(sourceRowId))) {
            return previousEntryIds;
          }

          const nextEntryIds = new Set(previousEntryIds);
          nextEntryIds.delete(String(sourceRowId));
          return nextEntryIds;
        });
      }
    },
    [
      alignedData,
      beginSyncActivity,
      businessCalendarContext,
      company_id,
      failSyncActivity,
      finishSyncActivity,
      formulaColumns,
      groupingSettings.nestSubmittals,
      id,
      reloadData,
      scheduleSyncContext,
      settingsInfo,
    ],
  );

  // Closes the right-side assignment drawer while keeping the content mounted for the exit animation.
  const handleCloseAssignDrawer = useCallback(() => {
    setIsAssignDrawerOpen(false);
  }, []);

  // Clears the drawer row state after the close animation finishes.
  const handleAssignDrawerOpenChange = useCallback((open) => {
    if (!open) {
      setAssignDrawerRowId("");
      setAssignDrawerSnapshotRow(null);
    }
  }, []);

  // Applies drawer-driven field updates back into the table's recalculation pipeline.
  function handleAssignDrawerColumnChange(changes = []) {
    if (!assignDrawerRowId || !Array.isArray(changes) || !changes.length) {
      return;
    }

    const rowPatch = changes.reduce((accumulator, change) => {
      if (!change?.name) {
        return accumulator;
      }

      accumulator[change.name] = change.value;
      return accumulator;
    }, {});

    applySingleRowPatch(assignDrawerRowId, rowPatch);
  }

  // Updates the row's responsible contractor from the assignment drawer.
  function handleAssignDrawerResponsibleContractorChange(value) {
    if (!assignDrawerRowId) {
      return;
    }

    applySingleRowPatch(assignDrawerRowId, {
      responsibleContractor: value,
    });
  }

  // Keeps the page-level dependency graph aligned with drawer dependency saves.
  const handleAssignDrawerDependenciesChange = useCallback(
    (nextDependencies) => {
      setDependencyGraph(nextDependencies || {});
    },
    [],
  );

  const assignDrawerWorkflowLockState = useMemo(
    () =>
      displayedAssignDrawerRow
        ? getWorkflowLockStateForRow(
            dependencyStateByEntryId,
            displayedAssignDrawerRow,
          )
        : null,
    [dependencyStateByEntryId, displayedAssignDrawerRow],
  );

  // Applies a single-row patch through the same recalculation and autosave path used by grid edits.
  const applySingleRowPatch = useCallback(
    (sourceRowId, rowPatch) => {
      if (!sourceRowId || !rowPatch || Object.keys(rowPatch).length === 0) {
        return;
      }

      const { recalculatedRows, changedRowUpdates } = applyLocalRowPatches({
        previousRows: gridRowsDataRef.current,
        rowPatches: new Map([[sourceRowId, rowPatch]]),
        formulaColumns,
        alignedData,
        scheduleSyncContext,
        settingsInfo,
        overwriteCalculations: groupingSettings.overwriteCalculations,
        businessCalendarContext,
      });

      gridRowsDataRef.current = recalculatedRows;
      setGridRowsData(recalculatedRows);
      const entryId = String(sourceRowId);
      const unconfirmedFields = Object.keys(rowPatch).filter(
        (field) => getCellEditFeedback(entryId, field)?.status === "unconfirmed",
      );
      if (unconfirmedFields.length && !entryId.startsWith("new-")) {
        let rowUpdate = changedRowUpdates.find(
          (update) => String(update.entryID) === entryId,
        );
        if (!rowUpdate) {
          rowUpdate = { entryID: entryId };
          changedRowUpdates.push(rowUpdate);
        }
        // An unchanged value still needs a request when its earlier save failed.
        unconfirmedFields.forEach((field) => {
          rowUpdate[field] = normalizePersistedFieldValue(
            field,
            rowPatch[field],
            alignedData,
          );
        });
      }
      void persistEditedRows(changedRowUpdates, new Map([[entryId, rowPatch]]));
    },
    [
      alignedData,
      formulaColumns,
      groupingSettings.overwriteCalculations,
      persistEditedRows,
      getCellEditFeedback,
      scheduleSyncContext,
      settingsInfo,
      businessCalendarContext,
    ],
  );

  const handleRetryCellEdit = useCallback(
    (entryId, field) => {
      const feedback = getCellEditFeedback(entryId, field);
      if (feedback?.status !== "unconfirmed") return;
      applySingleRowPatch(String(entryId), { [field]: feedback.value });
    },
    [applySingleRowPatch, getCellEditFeedback],
  );

  const columnDefs = useMemo(
    () =>
      buildColumnDefs(alignedData, {
        onToggleGroup: handleToggleGroup,
        onToggleAllGroups: handleToggleAllGroups,
        onToggleSort: handleToggleSort,
        onToggleSelect: handleRowSelect,
        getCheckState,
        isSelectionDisabled,
        allExpanded,
        sortModel,
        workingDays,
        nonWorkingDays,
        scheduleOptions,
        submittalsDropDown,
        onToggleCriticalPath: handleToggleCriticalPath,
        onOpenAssignDrawer: handleOpenAssignDrawer,
        onCloneSubmittal: handleCloneSubmittal,
        cloningEntryIds,
        onDeleteOrHideRow: handleDeleteOrHideRow,
        columnFilterModel,
        onSetColumnFilter: handleSetColumnFilter,
        columnPermissions,
        itemsPendingApprovals: liveItemsPendingApprovals,
        dependencyStateByEntryId,
        getEditingPresenceByCellKey: () => editingPresenceByCellKeyRef.current,
        getCellEditFeedback,
        onRetryCellEdit: handleRetryCellEdit,
      }),
    [
      alignedData,
      handleToggleGroup,
      handleToggleAllGroups,
      handleToggleSort,
      handleRowSelect,
      getCheckState,
      isSelectionDisabled,
      allExpanded,
      sortModel,
      workingDays,
      nonWorkingDays,
      scheduleOptions,
      submittalsDropDown,
      handleToggleCriticalPath,
      handleOpenAssignDrawer,
      handleCloneSubmittal,
      cloningEntryIds,
      handleDeleteOrHideRow,
      columnFilterModel,
      handleSetColumnFilter,
      columnPermissions,
      liveItemsPendingApprovals,
      dependencyStateByEntryId,
      getCellEditFeedback,
      handleRetryCellEdit,
    ],
  );

  // Returns the selected submittal entry IDs in the raw API format.
  const getSelectedEntryIds = useCallback(
    () => Object.keys(checkedRows).filter((entryID) => checkedRows[entryID]),
    [checkedRows],
  );

  // Returns the stable grid row ID for AG Grid virtualization and updates.
  const getRowId = useCallback((params) => params.data.id, []);
  // Marks division and subdivision rows to render through the full-width renderer.
  const isFullWidthRow = useCallback((params) => {
    const rowType = params.rowNode?.data?.rowType;
    return (
      rowType === "division" ||
      rowType === "subdivision" ||
      rowType === "subdivisionAddRow"
    );
  }, []);

  // Stores the current column order and sizing so edits do not reset the grid layout.
  const rememberColumnState = useCallback((params) => {
    if (!params.api) return;
    if (params.type === "columnResized" && params.finished === false) return;

    savedColumnStateRef.current = params.api.getColumnState();
  }, []);

  useEffect(() => {
    const api = gridRef.current?.api;
    const savedState = savedColumnStateRef.current;

    if (!api || !savedState?.length) {
      return;
    }

    api.applyColumnState({
      state: savedState,
      applyOrder: true,
    });
  }, [columnDefs]);

  useEffect(() => {
    const api = gridRef.current?.api;

    if (!api) {
      return;
    }

    requestAnimationFrame(() => {
      api.refreshCells({
        columns: WARNING_COLUMNS,
        force: true,
      });
    });
  }, [treeRows]);

  useEffect(() => {
    const api = gridRef.current?.api;

    if (!api) {
      return;
    }

    requestAnimationFrame(() => {
      api.redrawRows();
    });
  }, [selectedIds]);

  useEffect(() => {
    const api = gridRef.current?.api;

    if (!api) {
      return;
    }

    requestAnimationFrame(() => {
      api.refreshCells({
        columns: ["actualDeliveryDate"],
        force: true,
      });
    });
  }, [dependencyStateByEntryId]);

  useEffect(() => {
    editingPresenceByCellKeyRef.current = editingPresenceByCellKey;

    const api = gridRef.current?.api;

    if (!api) {
      return;
    }

    requestAnimationFrame(() => {
      api.refreshCells({
        force: true,
      });
    });
  }, [editingPresenceByCellKey]);

  // Hides the currently selected submittals through the legacy bulk-hide API.
  const handleBulkHide = useCallback(async () => {
    const entryIDs = getSelectedEntryIds();

    if (!entryIDs.length) {
      return;
    }

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    const requestData = {
      company_id,
      id,
      code,
      loginEmail,
      entryID: entryIDs,
    };
    const syncToken = beginSyncActivity("Hiding submittals");

    try {
      await HiddenItemsService.procurementLogHideEntry(requestData);
      removeProcurementRowsById(entryIDs, { clearSelection: true });
      finishSyncActivity(syncToken);
    } catch (error) {
      console.error("Failed to hide procurement rows", error);
      failSyncActivity(
        syncToken,
        "Failed to hide the selected item(s). You may no longer be synced to the latest version.",
      );
    }
  }, [
    beginSyncActivity,
    company_id,
    failSyncActivity,
    finishSyncActivity,
    getSelectedEntryIds,
    id,
    removeProcurementRowsById,
  ]);

  // Marks successfully posted submittals as Procore entries in the local grid.
  const handlePostSubmittalsSuccess = useCallback((updatedEntryIds) => {
    if (!updatedEntryIds?.length) {
      return;
    }

    const updatedEntryIdSet = new Set(updatedEntryIds.map(String));

    setGridRowsData((previousRows) => {
      const nextRows = previousRows.map((row) =>
        updatedEntryIdSet.has(firstFilled(row.entryID, row.tempId, row.id))
          ? { ...row, entryOrigin: "Procore" }
          : row,
      );
      gridRowsDataRef.current = nextRows;
      return nextRows;
    });
  }, []);

  // Flags the currently selected submittals as critical path through the legacy API.
  const handleBulkFlag = useCallback(async () => {
    const entryIDs = getSelectedEntryIds();

    if (!entryIDs.length) {
      return;
    }

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    const requestData = {
      company_id,
      id,
      code,
      loginEmail,
      entryID: entryIDs,
    };
    const syncToken = beginSyncActivity("Adding submittals to critical path");

    try {
      await CriticalPathFlagItemsService.procurementLogFlagCriticalPathEntry(
        requestData,
      );
      setProcurementCriticalPathState(entryIDs, true);
      finishSyncActivity(syncToken);
    } catch (error) {
      console.error("Failed to flag procurement rows", error);
      failSyncActivity(
        syncToken,
        "Failed to flag the selected item(s). You may no longer be synced to the latest version.",
      );
    }
  }, [
    beginSyncActivity,
    company_id,
    failSyncActivity,
    finishSyncActivity,
    getSelectedEntryIds,
    id,
    setProcurementCriticalPathState,
  ]);

  // Opens and resets the upload schedule modal from the actions menu.
  const openUploadScheduleModal = useCallback(() => {
    setIsUploadScheduleModalOpen(true);
  }, []);

  // Closes the upload schedule modal and clears any previous upload errors.
  const closeUploadScheduleModal = useCallback(() => {
    setIsUploadScheduleModalOpen(false);
  }, []);

  const openUploadProcurementLogModal = useCallback(() => {
    if (accessLevel >= 2) {
      setIsUploadProcurementLogModalOpen(true);
    }
  }, [accessLevel]);

  const closeUploadProcurementLogModal = useCallback(() => {
    setIsUploadProcurementLogModalOpen(false);
  }, []);

  // Handles top-bar action menu clicks that need page-local behavior.
  const handleActionsMenuClick = useCallback(
    ({ key }) => {
      if (key === "2") {
        openUploadProcurementLogModal();
        return;
      }

      if (key === "3") {
        openUploadScheduleModal();
      }
    },
    [openUploadProcurementLogModal, openUploadScheduleModal],
  );

  // Handles bulk menu clicks for legacy actions that are still simple to support here.
  const handleBulkMenuClick = useCallback(
    ({ key }) => {
      if (key === "2") {
        setIsBulkEditModalOpen(true);
        return;
      }

      if (key === "3") {
        void handleBulkHide();
        return;
      }

      if (key === "4") {
        void handleBulkFlag();
      }
    },
    [handleBulkHide, handleBulkFlag],
  );

  // Builds the bulk action menu shown when at least one submittal is selected.
  const actionsMenuItems = useMemo(
    () => [
      {
        label: "Upload Procurement Log",
        key: "2",
        icon: <UploadOutlined style={{ fontSize: "16px" }} />,
        disabled: accessLevel < 2,
      },
      {
        label: "Import Schedule",
        key: "3",
        icon: <UploadOutlined style={{ fontSize: "16px" }} />,
        disabled: accessLevel < 2,
      },
      {
        label: (
          <Link
            to={`${legacyProjectBasePath}/hidden-items`}
            style={{ textDecoration: "none" }}
          >
            View Hidden Items
          </Link>
        ),
        key: "4",
        icon: <EyeInvisibleOutlined style={{ fontSize: "15px" }} />,
        disabled: accessLevel < 2,
      },
      {
        label: (
          <Link
            to={`${legacyProjectBasePath}/flagged-items`}
            style={{ textDecoration: "none" }}
          >
            View Critical Path Items
          </Link>
        ),
        key: "5",
        icon: <FlagOutlined style={{ fontSize: "15px" }} />,
        disabled: accessLevel < 2,
      },
      {
        label: (
          <Link
            to={`${legacyProjectBasePath}/deleted-items`}
            style={{ textDecoration: "none" }}
          >
            View Deleted Items
          </Link>
        ),
        key: "6",
        icon: <HistoryOutlined style={{ fontSize: "15px" }} />,
        disabled: accessLevel < 3,
      },
      {
        label: (
          <Link
            to={`${legacyProjectBasePath}/view-dependencies`}
            style={{ textDecoration: "none" }}
          >
            View Gantt Chart
          </Link>
        ),
        key: "7",
        icon: <BoxPlotOutlined style={{ fontSize: "15px" }} />,
        disabled: accessLevel < 3,
      },
    ],
    [accessLevel, legacyProjectBasePath],
  );

  // Reuses the legacy distribution sidebar from the original procurement log.
  const exportDistributeMenuItems = useMemo(
    () => [
      {
        label: (
          <ExportProcurementLogPdf
            alignedData={alignedData}
            exportRows={exportRows}
            nestSubmittals={groupingSettings.nestSubmittals ? 1 : 0}
            projectAddress={projectAddress}
            projectInfo={projectName}
            projectsAccessLevelValue={accessLevel}
            approvalTime={approvalTime}
            reviewTime={reviewTime}
          />
        ),
        key: "1",
        disabled: accessLevel < 2 || isLoading,
      },
      {
        label: (
          <SidebarDistribution
            reportName="canned report - procurement-log"
            reportOwner="Koncurent"
            isProcLog={true}
            disabledButton={isLoading || accessLevel < 2}
            SelectViewOptions={SelectViewOptions}
            isLoading={isLoading}
          />
        ),
        key: "2",
        disabled: accessLevel < 2,
      },
    ],
    [
      SelectViewOptions,
      accessLevel,
      alignedData,
      approvalTime,
      exportRows,
      groupingSettings.nestSubmittals,
      isLoading,
      projectAddress,
      projectName,
      reviewTime,
    ],
  );

  // Builds the bulk action menu shown when at least one submittal is selected.
  const bulkMenuItems = useMemo(
    () => [
      {
        label: (
          <PostSubmittals
            projectDetails={projectDetails}
            projectsAccessLevelValue={accessLevel}
            checkedRows={checkedRows}
            setCheckedRows={setCheckedRows}
            onSuccess={handlePostSubmittalsSuccess}
          />
        ),
        key: "1",
        disabled: accessLevel < 2,
      },
      {
        label: "Bulk Edit/Assign",
        icon: <FormOutlined style={{ fontSize: "16px" }} />,
        key: "2",
      },
      {
        label: "Bulk Hide",
        icon: <EyeInvisibleOutlined style={{ fontSize: "16px" }} />,
        key: "3",
        disabled: accessLevel < 2,
      },
      {
        label: "Add to critical path",
        icon: <FlagOutlined style={{ fontSize: "16px" }} />,
        key: "4",
        disabled: accessLevel < 2,
      },
      {
        label: (
          <BulkDelete
            projectDetails={projectDetails}
            projectsAccessLevelValue={accessLevel}
            checkedRows={checkedRows}
            setCheckedRows={setCheckedRows}
            onDelete={handleBulkDeleteSuccess}
            itemsPendingApprovals={liveItemsPendingApprovals}
            beginSyncActivity={beginSyncActivity}
            finishSyncActivity={finishSyncActivity}
            failSyncActivity={failSyncActivity}
            allowDeleteNumberedSubmittals={true}
          />
        ),
        key: "5",
        disabled: accessLevel < 2,
        danger: true,
      },
    ],
    [
      projectDetails,
      accessLevel,
      checkedRows,
      setCheckedRows,
      handleBulkDeleteSuccess,
      liveItemsPendingApprovals,
      handlePostSubmittalsSuccess,
      beginSyncActivity,
      failSyncActivity,
      finishSyncActivity,
    ],
  );

  // Keeps the dropdown behavior aligned with the legacy procurement bulk actions.
  const actionsMenuProps = useMemo(
    () => ({
      items: actionsMenuItems,
      onClick: handleActionsMenuClick,
    }),
    [actionsMenuItems, handleActionsMenuClick],
  );

  // Keeps the export/distribute dropdown aligned with the legacy procurement log behavior.
  const exportDistributeMenuProps = useMemo(
    () => ({
      items: exportDistributeMenuItems,
    }),
    [exportDistributeMenuItems],
  );

  // Keeps the dropdown behavior aligned with the legacy procurement bulk actions.
  const bulkMenuProps = useMemo(
    () => ({
      items: bulkMenuItems,
      onClick: handleBulkMenuClick,
    }),
    [bulkMenuItems, handleBulkMenuClick],
  );

  // Saves the selected view and reloads the procurement log with that view's settings.
  const updateView = useCallback(
    (value) => {
      setSelectedView(value);

      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");
      const requestData = {
        company: company_id,
        projectID: id,
        code,
        loginEmail,
        viewName: value,
      };

      ProjectServices.setLastUsedView(requestData)
        .then(() => {
          reloadData?.();
        })
        .catch((error) => {
          console.log(error);
          setSyncStatus({
            state: "error",
            message:
              "Failed to update the selected view. You may no longer be synced to the latest version.",
          });
        });
    },
    [company_id, id, reloadData],
  );

  // Assigns managers and contractors to the selected submittals without reloading the table.
  const persistBulkAssignments = useCallback(
    async (assignmentData, assignmentRows) => {
      const managerEmails = assignmentData?.managerEmails || [];
      const contractorEmails = assignmentData?.contractorEmails || [];
      const action = assignmentData?.action || "";

      if (!managerEmails.length && !contractorEmails.length) {
        return;
      }

      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");
      const selectedItems = (assignmentRows || [])
        .map((row) => {
          const entryID = firstFilled(row.entryID);

          if (!entryID || String(entryID).startsWith("new-")) {
            return null;
          }

          return {
            id: entryID,
            submittalNumber: row.specNumber,
            submittalRevision: row.specRevision,
            submittalTitle: row.specTitle,
          };
        })
        .filter(Boolean);

      if (!selectedItems.length) {
        return;
      }
      const syncToken = beginSyncActivity("Saving assignments");

      try {
        await SideBarEndService.assignTask({
          code,
          loginEmail,
          company_id,
          project_id: id,
          managerEmails,
          contractorEmails,
          selectedItems,
          action,
        });
        finishSyncActivity(syncToken);
      } catch (error) {
        console.error("Failed to assign selected procurement rows", error);
        failSyncActivity(
          syncToken,
          "Failed to save assignments. You may no longer be synced to the latest version.",
        );
      }
    },
    [beginSyncActivity, company_id, failSyncActivity, finishSyncActivity, id],
  );

  // Removes managers and contractors from the selected submittals using the sidebar delete-task flow.
  const persistBulkAssignmentRemovals = useCallback(
    async (removalData, assignmentRows) => {
      const managerEmails = removalData?.managerEmails || [];
      const contractorEmails = removalData?.contractorEmails || [];

      if (!managerEmails.length && !contractorEmails.length) {
        return;
      }

      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");
      const selectedEntryIds = [...new Set(
        (assignmentRows || [])
          .map((row) => firstFilled(row.entryID))
          .filter(
            (entryID) => entryID && !String(entryID).startsWith("new-"),
          ),
      )];

      if (!selectedEntryIds.length) {
        return;
      }

      const assignedEmails = [...new Set([
        ...managerEmails.map(String),
        ...contractorEmails.map(String),
      ])];

      if (!assignedEmails.length) {
        return;
      }

      const syncToken = beginSyncActivity("Removing assignments");

      try {
        const deletePayload = selectedEntryIds.map((entryID) => ({
          code,
          loginEmail,
          company_id,
          project_id: id,
          id: entryID,
          assignedEmails,
        }));

        await SideBarEndService.deleteTask(deletePayload);
        finishSyncActivity(syncToken);
      } catch (error) {
        console.error(
          "Failed to remove assignments from selected procurement rows",
          error,
        );
        failSyncActivity(
          syncToken,
          "Failed to remove assignments. You may no longer be synced to the latest version.",
        );
      }
    },
    [beginSyncActivity, company_id, failSyncActivity, finishSyncActivity, id],
  );

  // Applies a bulk edit patch to the currently selected submittals using the table's normal update pipeline.
  const handleApplyBulkEdit = useCallback(
    async ({ rowPatch = {}, assignmentData, removalData } = {}) => {
      const selectedSubmittalIds = Array.from(selectedIdsRef.current).filter(
        (selectedId) => selectedId.startsWith("submittal:"),
      );
      const selectedSubmittalIdSet = new Set(
        selectedSubmittalIds.map((selectedId) =>
          selectedId.replace("submittal:", ""),
        ),
      );
      const hasRowPatch = Object.keys(rowPatch || {}).length > 0;
      const rowsForAssignmentSource = gridRowsDataRef.current;
      let rowsForAssignment = rowsForAssignmentSource.filter((row, index) =>
        selectedSubmittalIdSet.has(getProcurementRowId(row, index)),
      );

      if (!selectedSubmittalIds.length) {
        return;
      }

      if (hasRowPatch) {
        const rowPatches = new Map(
          selectedSubmittalIds.map((selectedId) => [
            selectedId.replace("submittal:", ""),
            rowPatch,
          ]),
        );
        const { recalculatedRows, changedRowUpdates } = applyLocalRowPatches({
          previousRows: gridRowsDataRef.current,
          rowPatches,
          formulaColumns,
          alignedData,
          scheduleSyncContext,
          settingsInfo,
          overwriteCalculations: groupingSettings.overwriteCalculations,
          businessCalendarContext,
        });

        gridRowsDataRef.current = recalculatedRows;
        setGridRowsData(recalculatedRows);
        rowsForAssignment = recalculatedRows.filter((row, index) =>
          selectedSubmittalIdSet.has(getProcurementRowId(row, index)),
        );
        void persistEditedRows(changedRowUpdates, rowPatches);
      }

      setIsBulkEditModalOpen(false);

      if (
        removalData?.managerEmails?.length ||
        removalData?.contractorEmails?.length
      ) {
        await persistBulkAssignmentRemovals(removalData, rowsForAssignment);
      }

      if (
        assignmentData?.managerEmails?.length ||
        assignmentData?.contractorEmails?.length
      ) {
        await persistBulkAssignments(assignmentData, rowsForAssignment);
      }
    },
    [
      formulaColumns,
      alignedData,
      scheduleSyncContext,
      settingsInfo,
      groupingSettings.overwriteCalculations,
      persistEditedRows,
      persistBulkAssignments,
      persistBulkAssignmentRemovals,
      businessCalendarContext,
    ],
  );

  // Applies the edit locally, recalculates affected rows, and saves the result.
  const handleCellValueChanged = useCallback(
    (params) => {
      if (params.data?.rowType !== "submittal") return;
      if (!params.colDef?.field) return;

      const sourceRowId = firstFilled(
        params.data.entryID,
        params.data.tempId,
        params.data.id,
      );

      if (
        params.colDef.field === "specNumber" &&
        params.newValue?.[SUBMITTAL_DROPDOWN_SELECTION_MARKER]
      ) {
        applySingleRowPatch(
          sourceRowId,
          buildSubmittalsDropDownRowPatch(params.newValue.sourceRow),
        );
        return;
      }

      applySingleRowPatch(sourceRowId, {
        [params.colDef.field]: params.newValue,
      });
    },
    [applySingleRowPatch],
  );

  const handleAddSubmittal = useCallback(
    async (subdivisionRow) => {
      const syncToken = beginSyncActivity("Creating submittal");
      try {
        const draftTemplate = await createNewSubmittalDraft(
          alignedData,
          subdivisionRow,
          company_id,
          id,
        );

        const nextDraft = applyFormulaColumnsToRow(
          draftTemplate,
          formulaColumns,
          alignedData,
          scheduleSyncContext,
          businessCalendarContext,
        );

        if (String(firstFilled(nextDraft.entryID)).startsWith("new-")) {
          pendingInsertedDraftsRef.current = [
            ...pendingInsertedDraftsRef.current,
            {
              tempId: firstFilled(nextDraft.entryID, nextDraft.tempId),
              signature: buildPendingInsertSignature(nextDraft),
              createdAt: Date.now(),
            },
          ];
        }

        // 4. Update state with the actual data object
        const renderedRowId = `submittal:${firstFilled(
          nextDraft.entryID,
          nextDraft.tempId,
        )}`;

        setGridRowsData((previousRows) => {
          const updatedRows = [...previousRows, nextDraft];
          gridRowsDataRef.current = updatedRows;
          return updatedRows;
        });

        if (normalizeText(searchValue)) {
          setSearchValue("");
        }

        // 5. Scroll and focus the new row
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const api = gridRef.current?.api;
            const rowNode = api?.getRowNode(renderedRowId);

            if (!api || !rowNode) {
              return;
            }

            api.ensureNodeVisible(rowNode, "middle");
            api.startEditingCell({
              rowIndex: rowNode.rowIndex,
              colKey: "specTitle",
            });
          });
        });
        finishSyncActivity(syncToken);
      } catch (error) {
        console.error(error);
        failSyncActivity(
          syncToken,
          "Failed to create a new submittal. You may no longer be synced to the latest version.",
        );
      }
    },
    [
      alignedData,
      beginSyncActivity,
      failSyncActivity,
      finishSyncActivity,
      formulaColumns,
      scheduleSyncContext,
      searchValue,
      company_id,
      id,
      businessCalendarContext,
    ],
  );

  // Returns the row height that matches each visual row type.
  const getRowHeight = useCallback((params) => {
    if (params.data?.rowType === "division") return 48;
    if (params.data?.rowType === "subdivision") return 44;
    if (params.data?.rowType === "subdivisionAddRow") return 40;
    return 45;
  }, []);

  // Styles selected and grouped rows to match the procurement log theme.
  const getRowStyle = useCallback(
    (params) => {
      if (selectedIds.has(params.data?.id)) {
        return {
          backgroundColor: "#eff6ff",
          borderBottom: "1px solid #dbeafe",
        };
      }

      if (params.data?.rowType === "division") {
        return {
          backgroundColor: "#f7fbff",
          borderBottom: "1px solid #dbe7f3",
        };
      }

      if (params.data?.rowType === "subdivision") {
        return {
          backgroundColor: "#fbfdff",
          borderBottom: "1px solid #edf2f7",
        };
      }

      if (params.data?.rowType === "subdivisionAddRow") {
        return {
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #edf2f7",
        };
      }

      return {
        borderBottom: "1px solid #f2f5f8",
      };
    },
    [selectedIds],
  );

  // Keeps full-width renderer params referentially stable so opening side UI does not refresh the grid.
  const fullWidthCellRendererParams = useMemo(
    () => ({
      onToggleGroup: handleToggleGroup,
      onAddSubmittal: handleAddSubmittal,
      onToggleSelect: handleRowSelect,
      getCheckState,
      isSelectionDisabled,
      selectedIds,
      accessLevel,
    }),
    [
      handleToggleGroup,
      handleAddSubmittal,
      handleRowSelect,
      getCheckState,
      isSelectionDisabled,
      selectedIds,
      accessLevel,
    ],
  );

  const isEmptyProcurementProject =
    !isLoading &&
    !loadError &&
    Array.isArray(divisions) &&
    divisions.length === 0;

  const hasNoAssignedSubmittals =
    !isLoading &&
    !loadError &&
    accessLevel === 1 &&
    selectableSubmittalRowIds?.size === 0;
  const showProcurementEmptyState =
    accessLevel === 1
      ? hasNoAssignedSubmittals
      : isEmptyProcurementProject;

  // Keeps the default column definition stable across unrelated page state changes.
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
    }),
    [],
  );

  // Blocks the beta procurement log entirely when the user lacks page-level access.
  if (!isLoading && accessLevel !== null && accessLevel < 1) {
    return (
      <div className={`d-flex flex-column ${styles.pageWrapper}`}>
        <div className="row w-100 d-flex justify-content-center p-5">
          <div className="d-flex flex-column align-items-center text-center gap-3">
            <LockRoundedIcon style={{ fontSize: "5rem", color: "#999" }} />
            <h6 className="mt-2 mb-0 text-muted">
              You do not have the necessary permissions to access this page.
            </h6>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isBulkEditModalOpen && selectedCount >= 1 && (
        <ProcurementBulkEditModal
          open={isBulkEditModalOpen}
          onCancel={() => setIsBulkEditModalOpen(false)}
          onApply={handleApplyBulkEdit}
          alignedData={alignedData}
          accessLevel={accessLevel}
          columnPermissions={columnPermissions}
          selectedCount={selectedCount}
          schedule={schedule}
          scheduleOptions={scheduleOptions}
          workingDays={workingDays}
          nonWorkingDays={nonWorkingDays}
        />
      )}

      <Drawer
        placement="right"
        width={520}
        open={isAssignDrawerOpen}
        onClose={handleCloseAssignDrawer}
        afterOpenChange={handleAssignDrawerOpenChange}
        destroyOnClose={false}
        className={styles.assignDrawer}
        title={
          displayedAssignDrawerRow ? (
            <div className={styles.assignDrawerTitle}>
              <span className={styles.assignDrawerHeading}>
                {firstFilled(
                  displayedAssignDrawerRow.specTitle,
                  displayedAssignDrawerRow.specNumber,
                  "Submittal Details",
                )}
              </span>
              <span className={styles.assignDrawerSubheading}>
                {[
                  firstFilled(displayedAssignDrawerRow.specNumber),
                  firstFilled(displayedAssignDrawerRow.specSection),
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </span>
            </div>
          ) : null
        }
      >
        {displayedAssignDrawerRow && (
          <ProcurementSideMenuContent
            content={displayedAssignDrawerRow}
            projectDetails={projectDetails}
            accessLevel={accessLevel}
            workingDays={workingDays}
            nonWorkingDays={nonWorkingDays}
            onResponsibleContractorChange={
              handleAssignDrawerResponsibleContractorChange
            }
            dependenciesGraph={dependencyGraph}
            setDependencies={handleAssignDrawerDependenciesChange}
            handleColumnChange={handleAssignDrawerColumnChange}
            actual={displayedAssignDrawerRow.actualDeliveryDate}
            isLocked={assignDrawerWorkflowLockState?.isLocked}
            workflowLockDetails={assignDrawerWorkflowLockState}
            beginSyncActivity={beginSyncActivity}
            finishSyncActivity={finishSyncActivity}
            failSyncActivity={failSyncActivity}
          />
        )}
      </Drawer>

      <ImportSchedule
        open={isUploadScheduleModalOpen}
        onCancel={closeUploadScheduleModal}
        onImportSuccess={reloadData}
        beginSyncActivity={beginSyncActivity}
        finishSyncActivity={finishSyncActivity}
        failSyncActivity={failSyncActivity}
      />

      <ImportProcurementLog
        open={accessLevel >= 2 && isUploadProcurementLogModalOpen}
        onCancel={closeUploadProcurementLogModal}
        onImportSuccess={reloadData}
        beginSyncActivity={beginSyncActivity}
        finishSyncActivity={finishSyncActivity}
        failSyncActivity={failSyncActivity}
      />

      <PostToProcoreModal
        record={checkedRows}
        isPostToProcoreVisible={isPostToProcoreVisible}
        setIsPostToProcoreVisible={setIsPostToProcoreVisible}
        isProcLog={true}
        projectID={id}
        projectDetails={projectDetails}
      />

      <ItemsShippedModal
        itemsShippedData={itemsShippedData}
        itemsShippedModal={itemsShippedModal}
        isitemsShippedLoading={isitemsShippedLoading}
        transfersToProjectInventoryHistory={transfersToProjectInventoryHistory}
        itemsShippedHistoryData={itemsShippedHistoryData}
        setItemsShippedModal={setItemsShippedModal}
        projectID={id}
        accessLevel={accessLevel}
        projectDetails={projectDetails}
        editedItems={[]}
        onTransferUpdated={reloadData}
      />

      <div className={`d-flex flex-column ${styles.pageWrapper}`}>
        <ProcurementTopBar
          company_id={company_id}
          project_id={id}
          accessLevel={accessLevel}
          selectedView={selectedView}
          viewOptions={SelectViewOptions}
          onUpdateView={updateView}
          exportDistributeMenuProps={exportDistributeMenuProps}
          actionsMenuProps={actionsMenuProps}
          isViewDisabled={isLoading || accessLevel < 1}
          isLoading={isLoading}
          itemsPendingApprovals={liveItemsPendingApprovals}
          deletedEntryIds={deletedEntryIds}
          projectName={projectName}
          showWarehouseTransfers={isSubscribedToWarehouse}
          isWarehouseTransfersDisabled={!canOpenWarehouseTransfers}
          onOpenWarehouseTransfers={() => setItemsShippedModal(true)}
          reloadData={reloadData}
          beginSyncActivity={beginSyncActivity}
          finishSyncActivity={finishSyncActivity}
          failSyncActivity={failSyncActivity}
        />

        <div
          className={`flex-grow-1 d-flex flex-column ${styles.tableCard} shadow-sm`}
        >
          <ProcurementQuickBar
            company_id={company_id}
            project_id={id}
            accessLevel={accessLevel}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            submittalsTotalCount={submittalsTotalCount}
            selectedCount={selectedCount}
            isSubscribedToWarehouse={isSubscribedToWarehouse}
            canCreatePurchaseOrder={canCreatePurchaseOrder}
            onOpenCreatePurchaseOrder={() => setIsPostToProcoreVisible(true)}
            bulkMenuProps={bulkMenuProps}
            onClearSelection={() => setSelectedIds(new Set())}
            activeFilterCount={activeFilterCount}
            onClearFilters={handleClearActiveFilters}
            showMyAccessibleToggle={
              accessLevel < 2 &&
              columnPermissions.selectableEntryIds instanceof Set
            }
            isMyAccessibleOnly={showAccessibleOnly}
            onToggleMyAccessibleOnly={() =>
              setShowAccessibleOnly((previousState) => !previousState)
            }
            advancedFilters={advancedFilters}
            onChangeAdvancedFilters={setAdvancedFilters}
            assignedFilterOptions={assignedFilterOptions}
            syncState={syncStatus.state}
            syncMessage={syncStatus.message}
            onRetrySync={handleRetrySync}
          />

          <div className={`flex-grow-1 ${styles.gridWrapper}`}>
            {showProcurementEmptyState ? (
              <div className={styles.emptyProjectState}>
                <div className={styles.emptyProjectContent}>
                  {accessLevel === 1 ? (
                    <>
                      <h3 className={styles.emptyProjectHeading}>
                        No submittals assigned to you
                      </h3>
                      <p className={styles.emptyProjectMessage}>
                        Your assigned submittals will appear here once your
                        project manager assigns them to you.
                      </p>
                    </>
                  ) : (
                    <>
                      {accessLevel >= 2 && (
                        <Button
                          className={styles.emptyProjectUploadButton}
                          icon={<UploadOutlined />}
                          onClick={openUploadProcurementLogModal}
                        >
                          Upload Procurement Log
                        </Button>
                      )}
                      <p className={styles.emptyProjectMessage}>
                        This project is empty. Click the button above to upload
                        your submittals and get started.
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className={`ag-theme-alpine w-100 h-100 ${styles.grid}`}>
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={treeRows}
                  getRowId={getRowId}
                  isFullWidthRow={isFullWidthRow}
                  fullWidthCellRenderer={ProcurementGroupFullWidthRow}
                  fullWidthCellRendererParams={fullWidthCellRendererParams}
                  defaultColDef={defaultColDef}
                  loadingOverlayComponent={LoadingComponent}
                  headerHeight={60}
                  getRowHeight={getRowHeight}
                  getRowStyle={getRowStyle}
                  onCellEditingStarted={handleCellEditingStarted}
                  onCellEditingStopped={handleCellEditingStopped}
                  onCellValueChanged={handleCellValueChanged}
                  onColumnMoved={rememberColumnState}
                  onColumnResized={rememberColumnState}
                  onBodyScroll={handleBodyScroll}
                  onFirstDataRendered={handleGridRowsReady}
                  onRowDataUpdated={handleGridRowsReady}
                  onGridPreDestroyed={handleGridPreDestroyed}
                  suppressScrollOnNewData={true}
                  maintainColumnOrder={true}
                  enableBrowserTooltips={true}
                  rowBuffer={20}
                  animateRows={false}
                  domLayout="normal"
                  loading={isLoading}
                  overlayNoRowsTemplate="<span>No submittals found</span>"
                  singleClickEdit={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ProcurementLogPage;
